import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import SalesDashboard from './SalesDashboard';
import AdminPage from './AdminPage';

// ── Colour palette (matches main app) ──
const C = {
  bg: '#0f1117', card: '#1a1d27', cardBorder: '#2a2d3a',
  purple: '#7c3aed', purpleLight: '#a78bfa', purpleDark: '#5b21b6',
  blue: '#38bdf8', cyan: '#22d3ee', green: '#4ade80', yellow: '#facc15',
  orange: '#fb923c', pink: '#f472b6', red: '#ef4444',
  text: '#e2e8f0', textMuted: '#94a3b8', textDim: '#64748b',
};

// Role hierarchy helper
const ROLE_LEVELS = { admin: 4, director: 3, manager: 2, sales_team: 1, none: 0 };
const canAccess = (user, minRole) => (ROLE_LEVELS[user?.role] || 0) >= (ROLE_LEVELS[minRole] || 0);

const fmtMoney = (v) => {
  if (v == null || v === 0) return '–';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const Loading = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: C.textDim }}>
    <div className="spinner" />
    <p style={{ marginTop: 16, fontSize: 13 }}>Loading...</p>
  </div>
);

// CSV export helper — downloads as .csv which opens in Google Sheets
const exportCSV = (filename, headers, rows) => {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel/Sheets
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ══════════════════════════════════════════════
// SIDEBAR NAV
// ══════════════════════════════════════════════

const CrmNav = ({ user, mobileOpen, onClose }) => {
  const location = useLocation();
  const [taskCount, setTaskCount] = useState(0);
  const isActive = (path) => path === '/crm'
    ? location.pathname === '/crm'
    : location.pathname.startsWith(path);

  useEffect(() => {
    fetch('/api/crm/tasks/summary').then(r => r.ok ? r.json() : {}).then(s => setTaskCount((s.active || 0) + (s.overdue || 0)));
    const iv = setInterval(() => {
      fetch('/api/crm/tasks/summary').then(r => r.ok ? r.json() : {}).then(s => setTaskCount((s.active || 0) + (s.overdue || 0)));
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const navItems = [
    { path: '/crm', icon: '⊞', label: 'Dashboard' },
    { path: '/crm/sales', icon: '📊', label: 'Sales Forecast' },
    { path: '/crm/clients', icon: '🏢', label: 'My Clients' },
    { path: '/crm/contacts', icon: '👤', label: 'My Contacts' },
    { path: '/crm/opportunities', icon: '💰', label: 'Opportunities' },
    { path: '/crm/tasks', icon: '☑', label: 'Tasks' },
    { path: '/crm/guide', icon: '📖', label: 'How to Guide' },
  ];

  const managerItems = canAccess(user, 'manager') ? [
    { path: '/crm/review', icon: '📋', label: 'Review Clients' },
    { path: '/crm/data-quality', icon: '✓', label: 'Data Quality' },
  ] : [];

  const handleNav = () => { if (onClose) onClose(); };

  return (
    <>
      {mobileOpen && <div className="crm-sidebar-backdrop" onClick={onClose} />}
      <div className={`crm-sidebar ${mobileOpen ? 'crm-sidebar-open' : ''}`}>
      <div className="crm-sidebar-brand">
        <img src="/tsl-logo.png" alt="TSL" className="crm-sidebar-logo" />
        <div className="crm-sidebar-title"></div>
        <button className="crm-sidebar-close" onClick={onClose}>✕</button>
      </div>
      <nav className="crm-nav">
        {navItems.map(item => (
          <Link key={item.path} to={item.path} onClick={handleNav}
            className={`crm-nav-item ${isActive(item.path) ? 'active' : ''}`}>
            <span className="crm-nav-icon">{item.icon}</span> {item.label}
            {item.path === '/crm/tasks' && taskCount > 0 && (
              <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
                padding: '1px 6px', borderRadius: 8, minWidth: 18, textAlign: 'center' }}>{taskCount}</span>
            )}
          </Link>
        ))}
        {managerItems.length > 0 && (
          <>
            <div style={{ borderTop: '1px solid #1e2130', margin: '8px 0' }} />
            {managerItems.map(item => (
              <Link key={item.path} to={item.path} onClick={handleNav}
                className={`crm-nav-item ${isActive(item.path) ? 'active' : ''}`}>
                <span className="crm-nav-icon">{item.icon}</span> {item.label}
              </Link>
            ))}
          </>
        )}
        <div style={{ borderTop: '1px solid #1e2130', margin: '8px 0' }} />
        <Link to="/crm/new-client" onClick={handleNav} className={`crm-nav-item ${isActive('/crm/new-client') ? 'active' : ''}`}>
          <span className="crm-nav-icon">➕</span> New Client
        </Link>
      </nav>
      <div className="crm-sidebar-bottom">
        {canAccess(user, 'manager') && (
          <Link to="/crm/admin" onClick={handleNav} className={`crm-nav-item ${isActive('/crm/admin') ? 'active' : ''}`}
            style={{ marginBottom: 12 }}>
            <span className="crm-nav-icon">⚙</span> Admin
          </Link>
        )}
        <div className="crm-sidebar-user-info">
          {user?.photo && <img src={user.photo} alt="" className="crm-user-avatar" />}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: C.textDim }}>
              {{ admin: 'Admin', director: 'Director', manager: 'Manager / BDM', sales_team: 'Sales Team' }[user?.role] || 'User'}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// ══════════════════════════════════════════════
// GLOBAL SEARCH
// ══════════════════════════════════════════════

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = React.useRef(null);
  const timerRef = React.useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = (q) => {
    if (q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    fetch(`/api/crm/search?q=${encodeURIComponent(q)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setResults(d); setOpen(true); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(v), 300);
  };

  const goTo = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const totalResults = results ? (results.clients.length + results.contacts.length + results.jobs.length + (results.opportunities || []).length) : 0;

  return (
    <div className="crm-global-search" ref={ref}>
      <input type="text" placeholder="Search clients, contacts, jobs, opportunities..."
        value={query} onChange={handleChange}
        onFocus={() => { if (results && query.length >= 2) setOpen(true); }}
        className="crm-search-input" style={{ width: 320 }} />
      {open && results && (
        <div className="crm-search-dropdown">
          {totalResults === 0 && !loading && (
            <div className="crm-search-empty">No results for "{query}"</div>
          )}
          {results.clients.length > 0 && (
            <div className="crm-search-section">
              <div className="crm-search-section-label">Clients</div>
              {results.clients.map(c => (
                <div key={c.CompanyCounter} className="crm-search-item"
                  onClick={() => goTo(`/crm/clients/${c.CompanyCounter}`)}>
                  <span className="crm-search-icon">🏢</span>
                  <div style={{ flex: 1 }}>
                    <div className="crm-search-item-title">{c.CompanyName}</div>
                    <div className="crm-search-item-sub">{c.Town || ''}{c.MainTelephone ? ` · ${c.MainTelephone}` : ''}</div>
                  </div>
                  {c.SpendYTD > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: C.confirmed }}>{fmtMoney(c.SpendYTD)}</span>}
                </div>
              ))}
            </div>
          )}
          {results.contacts.length > 0 && (
            <div className="crm-search-section">
              <div className="crm-search-section-label">Contacts</div>
              {results.contacts.map(c => (
                <div key={c.NameCounter} className="crm-search-item"
                  onClick={() => goTo(`/crm/contacts/${c.NameCounter}`)}>
                  <span className="crm-search-icon">👤</span>
                  <div style={{ flex: 1 }}>
                    <div className="crm-search-item-title">{c.FORENAME} {c.SURNAME}</div>
                    <div className="crm-search-item-sub">{c.CompanyName || ''}{c.EMAIL ? ` · ${c.EMAIL}` : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {results.jobs.length > 0 && (
            <div className="crm-search-section">
              <div className="crm-search-section-label">Jobs</div>
              {results.jobs.map((j, i) => (
                <div key={i} className="crm-search-item"
                  onClick={() => j.CompanyCounter ? goTo(`/crm/clients/${j.CompanyCounter}?tab=jobs`) : null}>
                  <span className="crm-search-icon">📋</span>
                  <div style={{ flex: 1 }}>
                    <div className="crm-search-item-title">{j.JobRef} — {j.JobTitle}</div>
                    <div className="crm-search-item-sub">{j.Client} · {j.DueOut} · {j.Status}</div>
                  </div>
                  {j.ConfirmedIncome > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: C.confirmed }}>{fmtMoney(j.ConfirmedIncome)}</span>}
                </div>
              ))}
            </div>
          )}
          {(results.opportunities || []).length > 0 && (
            <div className="crm-search-section">
              <div className="crm-search-section-label">Opportunities</div>
              {results.opportunities.map(o => (
                <div key={o.Id} className="crm-search-item"
                  onClick={() => goTo(`/crm/opportunities/${o.Id}`)}>
                  <span className="crm-search-icon">💰</span>
                  <div style={{ flex: 1 }}>
                    <div className="crm-search-item-title">{o.Title}</div>
                    <div className="crm-search-item-sub">{o.CompanyName || ''}{o.AssignedName ? ` · ${o.AssignedName}` : ''}</div>
                  </div>
                  <span className="crm-status-badge" style={{ background: o.StatusColor, color: '#fff', fontSize: 10, padding: '2px 6px' }}>{o.StatusName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════
// TOP BAR
// ══════════════════════════════════════════════

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchNotifs = useCallback(() => {
    fetch('/api/crm/notifications').then(r => r.ok ? r.json() : { notifications: [], unread: 0 })
      .then(d => { setNotifications(d.notifications || []); setUnread(d.unread || 0); });
  }, []);

  useEffect(() => { fetchNotifs(); const t = setInterval(fetchNotifs, 60000); return () => clearInterval(t); }, [fetchNotifs]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await fetch(`/api/crm/notifications/${id}/read`, { method: 'POST' });
    fetchNotifs();
  };

  const markAllRead = async () => {
    await fetch('/api/crm/notifications/read-all', { method: 'POST' });
    fetchNotifs();
  };

  const handleClick = (n) => {
    if (!n.IsRead) markRead(n.Id);
    if (n.LinkTo) { navigate(n.LinkTo); setOpen(false); }
  };

  const timeAgo = (d) => {
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.textMuted,
        position: 'relative', padding: '4px 8px',
      }}>
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 2, background: '#ef4444', color: '#fff',
            fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '1px 5px', minWidth: 16, textAlign: 'center',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 340,
          background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 1000, maxHeight: 420, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${C.cardBorder}` }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: 'none', border: 'none', color: C.purpleLight, cursor: 'pointer', fontSize: 12,
              }}>Mark all read</button>
            )}
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 320 }}>
            {notifications.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: C.textDim, fontSize: 13 }}>No notifications</div>
            )}
            {notifications.map(n => (
              <div key={n.Id} onClick={() => handleClick(n)} style={{
                padding: '10px 16px', cursor: n.LinkTo ? 'pointer' : 'default',
                borderBottom: `1px solid ${C.cardBorder}`,
                background: n.IsRead ? 'transparent' : 'rgba(124,58,237,0.06)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: n.IsRead ? 400 : 600, fontSize: 13, flex: 1 }}>{n.Title}</div>
                  {!n.IsRead && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.purple, flexShrink: 0, marginTop: 4, marginLeft: 8 }} />}
                </div>
                {n.Message && <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{n.Message}</div>}
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>{timeAgo(n.CreatedAt)}</div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${C.cardBorder}`, padding: '10px 16px', textAlign: 'center' }}>
            <button onClick={() => { navigate('/crm/notifications'); setOpen(false); }}
              style={{ background: 'none', border: 'none', color: C.purpleLight, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CrmNotifications = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch('/api/crm/notifications?limit=200').then(r => r.ok ? r.json() : { notifications: [] })
      .then(d => { setNotifications(d.notifications || []); setLoading(false); });
  }, []);

  const markRead = async (id) => {
    await fetch(`/api/crm/notifications/${id}/read`, { method: 'POST' });
    setNotifications(prev => prev.map(n => n.Id === id ? { ...n, IsRead: true } : n));
  };

  const markAllRead = async () => {
    await fetch('/api/crm/notifications/read-all', { method: 'POST' });
    setNotifications(prev => prev.map(n => ({ ...n, IsRead: true })));
  };

  const handleClick = (n) => {
    if (!n.IsRead) markRead(n.Id);
    if (n.LinkTo) navigate(n.LinkTo);
  };

  const timeAgo = (d) => {
    const mins = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.IsRead;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.IsRead).length;

  if (loading) return <Loading />;

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <button onClick={() => navigate('/crm')} className="crm-back-btn">← Dashboard</button>
      </CrmTopBar>
      <div className="crm-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>🔔 Notifications</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <button className="crm-btn-primary" onClick={markAllRead}
                style={{ fontSize: 12, padding: '6px 14px' }}>Mark all read ({unreadCount})</button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[{ key: 'all', label: 'All' }, { key: 'unread', label: 'Unread' }].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{
                padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: filter === f.key ? C.purple : 'transparent',
                color: filter === f.key ? '#fff' : C.textDim,
                border: `1px solid ${filter === f.key ? C.purple : C.cardBorder}`,
              }}>{f.label}</button>
          ))}
          <span style={{ color: C.textDim, fontSize: 12, alignSelf: 'center', marginLeft: 8 }}>{filtered.length} notifications</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filtered.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: C.textDim, fontSize: 14 }}>
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </div>
          )}
          {filtered.map(n => (
            <div key={n.Id} onClick={() => handleClick(n)} style={{
              padding: '14px 18px', cursor: n.LinkTo ? 'pointer' : 'default',
              background: n.IsRead ? C.card : 'rgba(124,58,237,0.08)',
              border: `1px solid ${n.IsRead ? C.cardBorder : C.purple + '44'}`,
              borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: n.IsRead ? 400 : 700, fontSize: 14 }}>{n.Title}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: C.textMuted }}>{timeAgo(n.CreatedAt)}</span>
                    {!n.IsRead && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.purple }} />}
                  </div>
                </div>
                {n.Message && <div style={{ fontSize: 13, color: C.textDim, marginTop: 4 }}>{n.Message}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const CrmTopBar = ({ user, children, backTo, backLabel, onMenuToggle }) => {
  const navigate = useNavigate();
  return (
  <div className="crm-topbar">
    <div className="crm-topbar-search" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {onMenuToggle && <button className="crm-hamburger" onClick={onMenuToggle}>☰</button>}
      {backTo && (
        <button onClick={() => navigate(backTo)} className="crm-link-btn" style={{ fontSize: 13 }}>
          ← {backLabel || 'Back'}
        </button>
      )}
      {children || <GlobalSearch />}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <NotificationBell />
      <button onClick={() => window.location.href = '/auth/logout'} className="crm-link-btn crm-link-btn-ghost">Sign Out</button>
    </div>
  </div>
  );
};

// ══════════════════════════════════════════════
// STAR RATING
// ══════════════════════════════════════════════

const StarRating = ({ rating, onChange, readOnly }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star}
          style={{
            cursor: readOnly ? 'default' : 'pointer',
            fontSize: 16,
            color: star <= (hover || rating) ? '#facc15' : '#2a2d3a',
            transition: 'color 0.1s',
          }}
          onMouseEnter={() => !readOnly && setHover(star)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange && onChange(star)}
        >★</span>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════
// MY CLIENTS PAGE
// ══════════════════════════════════════════════

const CrmClients = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('SpendYTD');
  const [sortDir, setSortDir] = useState('desc');
  const [ratings, setRatings] = useState({});
  const [myIds, setMyIds] = useState(new Set());
  const [allAssignments, setAllAssignments] = useState([]);
  const [viewMode, setViewMode] = useState('mine');
  const [subTypes, setSubTypes] = useState([]);
  const [companySubs, setCompanySubs] = useState({});
  const [subFilter, setSubFilter] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newCompany, setNewCompany] = useState({ companyName: '', town: '', telephone: '', email: '', address1: '', postcode: '' });
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [dragCol, setDragCol] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  const DEFAULT_COLS = [
    { key: 'CompanyName', label: 'CLIENT', align: 'left' },
    { key: 'SpendYTD', label: 'SPEND (YTD)', align: 'right' },
    { key: 'LastJobTitle', label: 'LAST JOB', align: 'left' },
    { key: 'LastActivityDate', label: 'LAST CONTACT', align: 'center' },
    { key: 'LifetimeValue', label: 'LIFETIME VALUE', align: 'right' },
    { key: 'Rating', label: 'RATING', align: 'center' },
  ];
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = window.sessionStorage?.getItem('crm-client-col-order');
      if (saved) {
        const keys = JSON.parse(saved);
        const ordered = keys.map(k => DEFAULT_COLS.find(c => c.key === k)).filter(Boolean);
        if (ordered.length === DEFAULT_COLS.length) return ordered;
      }
    } catch {}
    return DEFAULT_COLS;
  });
  const saveColOrder = (cols) => {
    setColOrder(cols);
    try { window.sessionStorage?.setItem('crm-client-col-order', JSON.stringify(cols.map(c => c.key))); } catch {}
  };
  const handleColDragStart = (idx) => setDragCol(idx);
  const handleColDragOver = (e, idx) => { e.preventDefault(); setDragOverColIdx(idx); };
  const handleColDrop = (idx) => {
    if (dragCol === null || dragCol === idx) { setDragCol(null); setDragOverColIdx(null); return; }
    const reordered = [...colOrder];
    const [moved] = reordered.splice(dragCol, 1);
    reordered.splice(idx, 0, moved);
    saveColOrder(reordered);
    setDragCol(null); setDragOverColIdx(null);
  };
  const handleColDragEnd = () => { setDragCol(null); setDragOverColIdx(null); };

  useEffect(() => {
    const fetches = [
      fetch('/api/crm/clients').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/ratings').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/my-assignments').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/all-assignments').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/subscription-types').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/company-subscriptions').then(r => r.ok ? r.json() : []),
    ];
    if (canAccess(user, 'manager')) {
      fetches.push(fetch('/api/crm/unassigned-clients').then(r => r.ok ? r.json() : { clients: [] }).then(d => d.clients || []));
      fetches.push(fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []));
    }
    Promise.all(fetches).then(([clientData, ratingData, myAssign, allAssign, types, compSubs, unassigned, users]) => {
      setClients(clientData);
      const ratMap = {};
      ratingData.forEach(r => { ratMap[r.CompanyCounter] = r.Rating; });
      setRatings(ratMap);
      setMyIds(new Set(myAssign));
      setAllAssignments(allAssign);
      setSubTypes(types);
      const subMap = {};
      compSubs.forEach(s => {
        if (!subMap[s.CompanyCounter]) subMap[s.CompanyCounter] = new Set();
        subMap[s.CompanyCounter].add(s.SubscriptionTypeId);
      });
      setCompanySubs(subMap);
      if (unassigned) setUnassignedClients(unassigned);
      if (users) setSalesUsers(users);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'CompanyName' ? 'asc' : 'desc'); }
  };

  const handleRate = (companyCounter, rating) => {
    setRatings(prev => ({ ...prev, [companyCounter]: rating }));
    fetch('/api/crm/rating', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter, rating }),
    });
  };

  const getAssignees = (cc) => {
    return allAssignments.filter(a => a.CompanyCounter === cc).map(a => a.DisplayName || a.AssignedTo.split('@')[0]);
  };

  const createCompany = async () => {
    if (!newCompany.companyName.trim()) return;
    const res = await fetch('/api/crm/create-company', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newCompany),
    });
    const data = await res.json();
    if (res.ok) {
      setShowCreate(false);
      setNewCompany({ companyName: '', town: '', telephone: '', email: '', address1: '', postcode: '' });
      if (data.companyCounter) {
        navigate(`/crm/clients/${data.companyCounter}`);
      } else {
        alert(`Client "${newCompany.companyName}" created.`);
      }
    } else {
      if (data.companyCounter) {
        navigate(`/crm/clients/${data.companyCounter}`);
      } else {
        alert(data.error || 'Failed to create');
      }
    }
  };

  const quickAssign = async (companyCounter, email) => {
    await fetch('/api/crm/assign-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter, assignTo: email }),
    });
    setUnassignedClients(prev => prev.filter(c => c.CompanyCounter !== companyCounter));
  };

  const toggleSelect = (cc, e) => {
    e.stopPropagation();
    setSelected(prev => { const s = new Set(prev); s.has(cc) ? s.delete(cc) : s.add(cc); return s; });
  };
  const selectAll = () => {
    const visible = sorted.slice(0, 200).map(c => c.CompanyCounter);
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible));
  };

  const bulkAssign = async (email) => {
    if (!email || selected.size === 0) return;
    await fetch('/api/crm/assign-clients-bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounters: [...selected], assignTo: email }),
    });
    const newAssign = [...selected].map(cc => ({ CompanyCounter: cc, AssignedTo: email, DisplayName: salesUsers.find(u => u.Email === email)?.DisplayName || email }));
    setAllAssignments(prev => [...prev, ...newAssign]);
    setSelected(new Set()); setBulkAction('');
    alert(`Assigned ${newAssign.length} clients`);
  };

  const bulkRate = async (rating) => {
    if (selected.size === 0) return;
    for (const cc of selected) {
      await fetch('/api/crm/rate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companyCounter: cc, rating }) });
    }
    setRatings(prev => { const r = { ...prev }; for (const cc of selected) r[cc] = rating; return r; });
    setSelected(new Set()); setBulkAction('');
    alert(`Rated ${selected.size} clients ★${rating}`);
  };

  const filtered = clients.filter(c => {
    if (viewMode === 'mine' && !myIds.has(c.CompanyCounter)) return false;
    if (subFilter && !(companySubs[c.CompanyCounter]?.has(subFilter))) return false;
    if (filterAssignedTo) {
      const assigned = allAssignments.filter(a => a.CompanyCounter === c.CompanyCounter).map(a => a.AssignedTo);
      if (!assigned.includes(filterAssignedTo)) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.CompanyName || '').toLowerCase().includes(s)
      || (c.Town || '').toLowerCase().includes(s)
      || (c.MainTelephone || '').toLowerCase().includes(s)
      || (c.EmailAddress || '').toLowerCase().includes(s)
      || (c.Accref || '').toLowerCase().includes(s)
      || (c.LastJobRef || '').toLowerCase().includes(s)
      || (c.LastJobTitle || '').toLowerCase().includes(s);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'Rating') { av = ratings[a.CompanyCounter] || 0; bv = ratings[b.CompanyCounter] || 0; }
    if (sortCol === 'LastActivityDate') { av = a.LastActivityDate ? new Date(a.LastActivityDate).getTime() : 0; bv = b.LastActivityDate ? new Date(b.LastActivityDate).getTime() : 0; }
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    av = (av || '').toString(); bv = (bv || '').toString();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  if (loading) return <Loading />;

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <input type="text" placeholder="Search clients..." value={search}
          onChange={e => setSearch(e.target.value)} className="crm-search-input" />
      </CrmTopBar>
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">{viewMode === 'mine' ? 'My Clients' : viewMode === 'unassigned' ? 'Unassigned Clients' : 'All Clients'}</h2>
            <p className="crm-page-subtitle">
              {viewMode === 'unassigned' ? `${unassignedClients.length} clients with spend but no AM` :
                `${filtered.length} companies${search ? ` matching "${search}"` : ''}${subFilter ? ` · filtered by ${subTypes.find(s => s.Id === subFilter)?.Name || ''}` : ''}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canAccess(user, 'manager') && <button className="crm-btn-primary" onClick={() => {
              const getAssignees = (cc) => allAssignments.filter(a => a.CompanyCounter === cc).map(a => a.DisplayName || a.AssignedTo).join(', ');
              exportCSV(`clients-${new Date().toISOString().split('T')[0]}.csv`,
                ['Company', 'Town', 'Telephone', 'Spend YTD', 'Lifetime Value', 'Last Job', 'Last Job Ref', 'Last Contact', 'Rating', 'Assigned To'],
                sorted.map(c => [c.CompanyName, c.Town, c.MainTelephone, c.SpendYTD || 0, c.LifetimeValue || 0, c.LastJobTitle, c.LastJobRef, c.LastActivityDate ? new Date(c.LastActivityDate).toLocaleDateString('en-GB') : '', ratings[c.CompanyCounter] || '', getAssignees(c.CompanyCounter)])
              );
            }} style={{ fontSize: 12, padding: '6px 14px', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}>
              ↓ Export CSV
            </button>}
            <button className="crm-btn-primary" onClick={() => setShowCreate(!showCreate)} style={{ fontSize: 12, padding: '6px 14px' }}>
              {showCreate ? '✕ Cancel' : '+ New Client'}
            </button>
            <div className="crm-view-toggle">
              <button className={`crm-toggle-btn ${viewMode === 'mine' ? 'active' : ''}`}
                onClick={() => setViewMode('mine')}>My Clients ({myIds.size})</button>
              <button className={`crm-toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
                onClick={() => setViewMode('all')}>All Clients</button>
              {canAccess(user, 'manager') && unassignedClients.length > 0 && (
                <button className={`crm-toggle-btn ${viewMode === 'unassigned' ? 'active' : ''}`}
                  style={viewMode !== 'unassigned' ? { color: '#fb923c' } : {}}
                  onClick={() => setViewMode('unassigned')}>Unassigned ({unassignedClients.length})</button>
              )}
            </div>
          </div>
        </div>

        {/* Create client form */}
        {showCreate && (
          <div className="crm-create-card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New Client</h3>
            <p style={{ fontSize: 12, color: C.textDim, marginBottom: 12 }}>Creates the company in HireTrack and the CRM.</p>
            <div className="crm-create-grid">
              <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                <label>Company Name *</label>
                <input type="text" value={newCompany.companyName} onChange={e => setNewCompany({ ...newCompany, companyName: e.target.value })}
                  placeholder="e.g. Acme Events Ltd" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Town</label>
                <input type="text" value={newCompany.town} onChange={e => setNewCompany({ ...newCompany, town: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Phone</label>
                <input type="text" value={newCompany.telephone} onChange={e => setNewCompany({ ...newCompany, telephone: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Email</label>
                <input type="email" value={newCompany.email} onChange={e => setNewCompany({ ...newCompany, email: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Postcode</label>
                <input type="text" value={newCompany.postcode} onChange={e => setNewCompany({ ...newCompany, postcode: e.target.value })} className="crm-input" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <button className="crm-btn-primary" onClick={createCompany}
                  disabled={!newCompany.companyName.trim()} style={{ opacity: newCompany.companyName.trim() ? 1 : 0.4 }}>
                  Create Client
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unassigned clients view */}
        {viewMode === 'unassigned' && (
          <div className="crm-table-card">
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th className="crm-th" style={{ textAlign: 'left' }}>CLIENT</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>SPEND (YTD)</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>LAST JOB</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>LIFETIME VALUE</th>
                    <th className="crm-th" style={{ textAlign: 'center' }}>ASSIGN TO</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedClients.map(c => (
                    <tr key={c.CompanyCounter} className="crm-tr">
                      <td className="crm-td">
                        <div className="crm-td-main" style={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>{c.CompanyName}</div>
                        <div className="crm-td-sub">{c.Town || ''}{c.MainTelephone ? ` · ${c.MainTelephone}` : ''}</div>
                      </td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.SpendYTD)}</td>
                      <td className="crm-td">
                        <div style={{ fontSize: 12 }}>{c.LastJob || '–'}</div>
                        {c.LastJobDate && <div className="crm-td-sub">{new Date(c.LastJobDate).toLocaleDateString('en-GB')}</div>}
                      </td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.LifetimeValue)}</td>
                      <td className="crm-td" style={{ textAlign: 'center' }}>
                        <select className="crm-filter-select" style={{ fontSize: 11 }}
                          value="" onChange={e => quickAssign(c.CompanyCounter, e.target.value)}>
                          <option value="">Assign...</option>
                          {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
                        </select>
                      </td>
                      <td className="crm-td" style={{ color: C.textDim, cursor: 'pointer' }}
                        onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewMode !== 'unassigned' && <>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: subTypes.length > 0 ? 0 : 12 }}>
          {canAccess(user, 'manager') && (
            <select className="crm-filter-select" value={filterAssignedTo} onChange={e => setFilterAssignedTo(e.target.value)}>
              <option value="">All People</option>
              {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
            </select>
          )}
        </div>
        {/* Subscription filters */}
        {subTypes.length > 0 && (
          <div className="crm-sub-filters">
            <span style={{ fontSize: 12, color: C.textDim, marginRight: 4 }}>Filter:</span>
            {subTypes.map(st => (
              <button key={st.Id} className={`crm-sub-filter-btn ${subFilter === st.Id ? 'active' : ''}`}
                style={subFilter === st.Id ? { background: st.Color || C.purple, borderColor: st.Color || C.purple, color: '#fff' } : {}}
                onClick={() => setSubFilter(subFilter === st.Id ? null : st.Id)}>
                {st.Name}
              </button>
            ))}
            {subFilter && (
              <button className="crm-sub-filter-clear" onClick={() => setSubFilter(null)}>✕ Clear</button>
            )}
          </div>
        )}

        {viewMode === 'mine' && myIds.size === 0 && !search && !subFilter && (
          <div className="crm-empty-state">
            <div className="crm-empty-icon">🏢</div>
            <h3>No clients assigned yet</h3>
            <p>Your manager will assign clients to you. In the meantime, you can browse all clients.</p>
          </div>
        )}

        {(viewMode === 'all' || myIds.size > 0 || search || subFilter) && (
          <>
          {/* Bulk action bar */}
          {canAccess(user, 'manager') && selected.size > 0 && (
            <div style={{
              background: C.purple, borderRadius: 10, padding: '12px 20px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>
                {selected.size} selected
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, background: '#2a1f4e', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', fontSize: 12 }}>
                  <option value="" style={{ background: '#2a1f4e', color: '#fff' }}>Choose action...</option>
                  <option value="assign" style={{ background: '#2a1f4e', color: '#fff' }}>Assign to...</option>
                  <option value="rate" style={{ background: '#2a1f4e', color: '#fff' }}>Set rating...</option>
                  <option value="subscribe" style={{ background: '#2a1f4e', color: '#fff' }}>Add tag...</option>
                  <option value="unsubscribe" style={{ background: '#2a1f4e', color: '#fff' }}>Remove tag...</option>
                </select>
                {bulkAction === 'assign' && (
                  <select onChange={e => { if (e.target.value) bulkAssign(e.target.value); }}
                    style={{ padding: '6px 10px', borderRadius: 6, background: '#2a1f4e', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', fontSize: 12 }}>
                    <option value="" style={{ background: '#2a1f4e', color: '#fff' }}>Select person...</option>
                    {salesUsers.map(u => <option key={u.Email} value={u.Email} style={{ background: '#2a1f4e', color: '#fff' }}>{u.DisplayName || u.Email}</option>)}
                  </select>
                )}
                {bulkAction === 'rate' && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[1,2,3,4,5].map(r => (
                      <button key={r} onClick={() => bulkRate(r)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#facc15', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 14 }}>
                        {'★'.repeat(r)}
                      </button>
                    ))}
                  </div>
                )}
                {(bulkAction === 'subscribe' || bulkAction === 'unsubscribe') && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {subTypes.map(st => (
                      <button key={st.Id} onClick={async () => {
                        const isSub = bulkAction === 'subscribe';
                        await fetch('/api/crm/company-subscription-bulk', {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ companyCounters: [...selected], subscriptionTypeId: st.Id, isSubscribed: isSub }),
                        });
                        setCompanySubs(prev => {
                          const next = { ...prev };
                          for (const cc of selected) {
                            if (!next[cc]) next[cc] = new Set();
                            else next[cc] = new Set(next[cc]);
                            isSub ? next[cc].add(st.Id) : next[cc].delete(st.Id);
                          }
                          return next;
                        });
                        alert(`${isSub ? 'Added' : 'Removed'} "${st.Name}" ${isSub ? 'to' : 'from'} ${selected.size} clients`);
                        setSelected(new Set()); setBulkAction('');
                      }}
                        style={{ background: st.Color || 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                        {st.Name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => { setSelected(new Set()); setBulkAction(''); }}
                style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12 }}>
                ✕ Clear
              </button>
            </div>
          )}
          <div className="crm-table-card">
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    {canAccess(user, 'manager') && (
                      <th className="crm-th" style={{ width: 36, textAlign: 'center' }}>
                        <input type="checkbox" checked={selected.size > 0 && selected.size === sorted.slice(0, 200).length}
                          onChange={selectAll} style={{ cursor: 'pointer', accentColor: C.purple }} />
                      </th>
                    )}
                    {colOrder.map((col, ci) => (
                      <th key={col.key} className={`crm-th ${sortCol === col.key ? 'crm-th-active' : ''}`}
                        style={{ textAlign: col.align, cursor: 'grab', userSelect: 'none',
                          borderLeft: dragOverColIdx === ci && dragCol !== ci ? `2px solid ${C.purple}` : undefined,
                          opacity: dragCol === ci ? 0.5 : 1,
                        }}
                        draggable onDragStart={() => handleColDragStart(ci)}
                        onDragOver={(e) => handleColDragOver(e, ci)} onDrop={() => handleColDrop(ci)}
                        onDragEnd={handleColDragEnd}
                        onClick={() => handleSort(col.key)}>
                        {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </th>
                    ))}
                    <th className="crm-th">ASSIGNED TO</th>
                    <th style={{ width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.slice(0, 200).map(c => {
                    const assignees = getAssignees(c.CompanyCounter);
                    const isSel = selected.has(c.CompanyCounter);
                    return (
                      <tr key={c.CompanyCounter} className="crm-tr" style={{ cursor: 'pointer', background: isSel ? 'rgba(124,58,237,0.1)' : undefined }}
                        onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                        {canAccess(user, 'manager') && (
                          <td className="crm-td" style={{ textAlign: 'center' }} onClick={e => toggleSelect(c.CompanyCounter, e)}>
                            <input type="checkbox" checked={isSel} readOnly style={{ cursor: 'pointer', accentColor: C.purple }} />
                          </td>
                        )}
                        {colOrder.map(col => {
                          if (col.key === 'CompanyName') return (
                            <td key={col.key} className="crm-td">
                              <div style={{ fontWeight: 600 }}>{c.CompanyName}</div>
                              <div className="crm-td-sub">{c.Town || ''}{c.MainTelephone ? ` · ${c.MainTelephone}` : ''}</div>
                            </td>
                          );
                          if (col.key === 'SpendYTD') return (
                            <td key={col.key} className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.SpendYTD)}</td>
                          );
                          if (col.key === 'LastJobTitle') return (
                            <td key={col.key} className="crm-td">
                              <div>{c.LastJobTitle || '–'}</div>
                              <div className="crm-td-sub">{c.LastJobRef ? `${c.LastJobRef} · ${c.LastJobDate || ''}` : ''}</div>
                            </td>
                          );
                          if (col.key === 'LastActivityDate') return (
                            <td key={col.key} className="crm-td" style={{ textAlign: 'center' }}>
                              {c.LastActivityDate ? (() => {
                                const days = Math.floor((Date.now() - new Date(c.LastActivityDate).getTime()) / 86400000);
                                const color = days <= 7 ? '#4ade80' : days <= 30 ? '#fb923c' : '#ef4444';
                                const label = days === 0 ? 'Today' : days === 1 ? '1d ago' : `${days}d ago`;
                                return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: color + '20', color }}>{label}</span>;
                              })() : <span style={{ fontSize: 11, color: C.textDim }}>–</span>}
                            </td>
                          );
                          if (col.key === 'LifetimeValue') return (
                            <td key={col.key} className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.LifetimeValue)}</td>
                          );
                          if (col.key === 'Rating') return (
                            <td key={col.key} className="crm-td" style={{ textAlign: 'center' }}>
                              <StarRating rating={ratings[c.CompanyCounter] || 0}
                                onChange={(r) => { handleRate(c.CompanyCounter, r); }} />
                            </td>
                          );
                          return null;
                        })}
                        <td className="crm-td">
                          {assignees.length > 0
                            ? <span className="crm-assignee-tags">{assignees.map((a, i) => (
                                <span key={i} className="crm-assignee-tag">{a.split(' ')[0]}</span>
                              ))}</span>
                            : <span style={{ color: C.textDim, fontSize: 11 }}>Unassigned</span>}
                        </td>
                        <td className="crm-td" style={{ color: C.textDim }}>›</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sorted.length > 200 && (
              <div className="crm-table-footer">
                Showing 200 of {sorted.length}. Use search to filter.
              </div>
            )}
          </div>
          </>
        )}
        </>}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// CLIENT DETAIL PAGE
// ══════════════════════════════════════════════

const CrmClientDetail = ({ user, onMenuToggle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [subTypes, setSubTypes] = useState([]);
  const [subs, setSubs] = useState({});
  const [jobSearch, setJobSearch] = useState('');
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const [tab, setTab] = useState(tabParam === 'jobs' ? 'jobs' : 'overview');
  const [assignees, setAssignees] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [clientOpps, setClientOpps] = useState([]);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [editingClient, setEditingClient] = useState(false);
  const [editClientData, setEditClientData] = useState({});
  const [jobSortCol, setJobSortCol] = useState('DueOut');
  const [jobSortDir, setJobSortDir] = useState('desc');

  useEffect(() => {
    Promise.all([
      fetch(`/api/crm/client/${id}`).then(r => r.ok ? r.json() : null),
      fetch('/api/crm/subscription-types').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/all-assignments').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []),
      fetch(`/api/crm/opportunities?companyCounter=${id}`).then(r => r.ok ? r.json() : []),
      fetch(`/api/crm/pinned-notes/${id}`).then(r => r.ok ? r.json() : []),
      fetch(`/api/crm/health-score/${id}`).then(r => r.ok ? r.json() : null),
    ]).then(([detail, types, allAssign, users, opps, notes, health]) => {
      if (detail) {
        setData(detail);
        setRating(detail.rating || 0);
        const subMap = {};
        detail.subscriptions.forEach(s => { subMap[s.SubscriptionTypeId] = true; });
        setSubs(subMap);
      }
      setSubTypes(types);
      setAssignees(allAssign.filter(a => a.CompanyCounter === parseInt(id)));
      setSalesUsers(users || []);
      setClientOpps(opps || []);
      setPinnedNotes(notes || []);
      setHealthScore(health);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const handleRate = (r) => {
    setRating(r);
    fetch('/api/crm/rating', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter: parseInt(id), rating: r }),
    });
  };

  const toggleSub = (subTypeId) => {
    const current = subs[subTypeId] || false;
    setSubs(prev => ({ ...prev, [subTypeId]: !current }));
    fetch('/api/crm/company-subscription', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter: parseInt(id), subscriptionTypeId: subTypeId, isSubscribed: !current }),
    });
  };

  const assignUser = (email) => {
    if (!email || assignees.find(a => a.AssignedTo === email)) return;
    fetch('/api/crm/assign-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter: parseInt(id), assignTo: email }),
    });
    const u = salesUsers.find(s => s.Email === email);
    setAssignees(prev => [...prev, { CompanyCounter: parseInt(id), AssignedTo: email, DisplayName: u?.DisplayName || email }]);
  };

  const unassignUser = (email) => {
    fetch('/api/crm/unassign-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter: parseInt(id), assignedTo: email }),
    });
    setAssignees(prev => prev.filter(a => a.AssignedTo !== email));
  };

  const startEditClient = () => {
    setEditingClient(true);
    setEditClientData({
      companyName: company.CompanyName || '', mainTelephone: company.MainTelephone || '',
      emailAddress: company.EmailAddress || '', cellular: company.Cellular || '',
      mainFax: company.MainFax || '', url: company.URL || '',
      accountsTelephone: company.AccountsTelephone || '', accountsEmail: company.AccountsEmail || '',
      address1: company.Address1 || '', address2: company.Address2 || '', address3: company.Address3 || '',
      town: company.Town || '', county: company.County || '', postCode: company.PostCode || '',
      country: company.Country || '', vatNumber: company.VatNumber || '',
    });
  };

  const saveEditClient = async () => {
    const res = await fetch(`/api/crm/client/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editClientData),
    });
    const result = await res.json();
    if (res.ok) {
      const detail = await fetch(`/api/crm/client/${id}`).then(r => r.ok ? r.json() : null);
      if (detail) {
        setData(detail);
        setRating(detail.rating || 0);
        const subMap = {};
        detail.subscriptions.forEach(s => { subMap[s.SubscriptionTypeId] = true; });
        setSubs(subMap);
      }
      setEditingClient(false);
      if (result.nexusPushed === false) {
        alert('Saved to CRM but could not push to HireTrack — it will sync on next scheduled sync.');
      }
    } else {
      alert(result.error || 'Failed to save');
    }
  };

  if (loading) return <Loading />;
  if (!data) return (
    <div className="crm-content">
      <p style={{ color: C.textDim }}>Client not found.</p>
      <Link to="/crm/clients" style={{ color: C.purple }}>← Back to clients</Link>
    </div>
  );

  const { company, contacts, jobs } = data;

  const filteredJobs = jobs.filter(j => {
    if (!jobSearch) return true;
    const s = jobSearch.toLowerCase();
    return (j.JobRef || '').toLowerCase().includes(s)
      || (j.JobTitle || '').toLowerCase().includes(s)
      || (j.Status || '').toLowerCase().includes(s)
      || (j.JobType || '').toLowerCase().includes(s);
  });

  const moneyKeys = new Set(['QuoteIncome', 'ProvisionalIncome', 'ConfirmedIncome', 'InvoicedValue']);
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    let va = a[jobSortCol], vb = b[jobSortCol];
    if (moneyKeys.has(jobSortCol)) { va = Number(va) || 0; vb = Number(vb) || 0; }
    else if (jobSortCol === 'DueOut') { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
    else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
    if (va < vb) return jobSortDir === 'asc' ? -1 : 1;
    if (va > vb) return jobSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleJobSort = (col) => {
    if (jobSortCol === col) setJobSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setJobSortCol(col); setJobSortDir(moneyKeys.has(col) || col === 'DueOut' ? 'desc' : 'asc'); }
  };

  const jobTotals = filteredJobs.reduce((acc, j) => ({
    quote: acc.quote + (Number(j.QuoteIncome) || 0),
    prov: acc.prov + (Number(j.ProvisionalIncome) || 0),
    conf: acc.conf + (Number(j.ConfirmedIncome) || 0),
    inv: acc.inv + (Number(j.InvoicedValue) || 0),
  }), { quote: 0, prov: 0, conf: 0, inv: 0 });

  const avatarColors = [C.purple, C.blue, C.green, C.orange, C.pink, C.cyan];
  const getColor = (id) => avatarColors[id % avatarColors.length];
  const initials = (f, s) => ((f || '').charAt(0) + (s || '').charAt(0)).toUpperCase() || '?';

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <button onClick={() => navigate('/crm/clients')} className="crm-back-btn">← Clients</button>
      </CrmTopBar>
      <div className="crm-content">
        {/* Header */}
        <div className="crm-detail-header">
          <div className="crm-detail-avatar" style={{ background: C.purple }}>
            {(company.CompanyName || '??').substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 className="crm-detail-name" style={{ margin: 0 }}>{company.CompanyName}</h2>
              {healthScore && (
                <span title={`Health: ${healthScore.grade} (${healthScore.score}/100)`}
                  style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: healthScore.gradeColor + '20', color: healthScore.gradeColor, whiteSpace: 'nowrap' }}>
                  {healthScore.score >= 80 ? '💚' : healthScore.score >= 60 ? '💙' : healthScore.score >= 40 ? '🟡' : '🔴'} {healthScore.score}/100
                </span>
              )}
            </div>
            <div className="crm-detail-meta">
              {company.Town && <span>{company.Town}</span>}
              {company.Accref && <span>Acc: {company.Accref}</span>}
              {company.RegNumber && <span>Reg: {company.RegNumber}</span>}
              {data.lastContact ? (() => {
                const days = Math.floor((Date.now() - new Date(data.lastContact.ActivityDate).getTime()) / 86400000);
                const color = days <= 7 ? '#4ade80' : days <= 30 ? '#fb923c' : '#ef4444';
                const label = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
                const type = data.lastContact.ActivityType === 'call' ? '📞' : data.lastContact.ActivityType === 'email' ? '✉️' : data.lastContact.ActivityType === 'meeting' ? '🤝' : '📋';
                const who = data.lastContact.CreatedBy ? data.lastContact.CreatedBy.split('@')[0] : '';
                return <span style={{ color, fontWeight: 600 }}>{type} Last contacted: {label}{who ? ` by ${who}` : ''}</span>;
              })() : <span style={{ color: '#ef4444', fontWeight: 600 }}>No contact logged</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
              {!editingClient && (
                <button className="crm-btn-primary" onClick={startEditClient}
                  style={{ fontSize: 12, padding: '6px 14px' }}>✏️ Edit</button>
              )}
            </div>
            <StarRating rating={rating} onChange={handleRate} />
            {/* Assigned to */}
            <div style={{ marginTop: 8 }}>
              {assignees.length > 0 ? (
                <div className="crm-detail-assignees">
                  {assignees.map(a => (
                    <span key={a.AssignedTo} className="crm-assignee-tag" style={{ fontSize: 12, padding: '3px 10px' }}>
                      {a.DisplayName || a.AssignedTo.split('@')[0]}
                      {canAccess(user, 'manager') && (
                        <span className="crm-assignee-remove" onClick={() => unassignUser(a.AssignedTo)}>×</span>
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: 12, color: C.textDim }}>Unassigned</span>
              )}
            </div>
            {/* Admin: assign dropdown */}
            {canAccess(user, 'manager') && (
              <div style={{ marginTop: 8 }}>
                <select className="crm-filter-select" style={{ fontSize: 12 }}
                  value="" onChange={e => { assignUser(e.target.value); e.target.value = ''; }}>
                  <option value="">+ Assign to...</option>
                  {salesUsers.filter(u => !assignees.find(a => a.AssignedTo === u.Email)).map(u => (
                    <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="crm-detail-subs" style={{ marginTop: 8 }}>
              {subTypes.map(st => (
                <SubTag key={st.Id} label={st.Name} active={subs[st.Id] || false} color={st.Color}
                  onClick={() => toggleSub(st.Id)} />
              ))}
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="crm-detail-kpis">
          <div className="crm-detail-kpi">
            <div className="crm-detail-kpi-label">SPEND (YTD)</div>
            <div className="crm-detail-kpi-value" style={{ color: C.confirmed }}>{fmtMoney(company.SpendYTD)}</div>
          </div>
          <div className="crm-detail-kpi">
            <div className="crm-detail-kpi-label">LIFETIME VALUE</div>
            <div className="crm-detail-kpi-value" style={{ color: C.green }}>{fmtMoney(company.LifetimeValue)}</div>
          </div>
          <div className="crm-detail-kpi">
            <div className="crm-detail-kpi-label">CONTACTS</div>
            <div className="crm-detail-kpi-value">{contacts.length}</div>
          </div>
          <div className="crm-detail-kpi">
            <div className="crm-detail-kpi-label">TOTAL JOBS</div>
            <div className="crm-detail-kpi-value">{jobs.length}</div>
          </div>
        </div>

        {/* Pinned Notes */}
        <div style={{ marginBottom: 16 }}>
          {pinnedNotes.map(n => (
            <div key={n.Id} style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 8, position: 'relative' }}>
              {editingNote === n.Id ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea defaultValue={n.Note} id={`edit-note-${n.Id}`}
                    style={{ flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, fontSize: 13, color: '#1f2937', minHeight: 40, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button onClick={async () => {
                      const v = document.getElementById(`edit-note-${n.Id}`).value;
                      await fetch(`/api/crm/pinned-notes/${n.Id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: v }) });
                      setPinnedNotes(prev => prev.map(p => p.Id === n.Id ? { ...p, Note: v } : p));
                      setEditingNote(null);
                    }} style={{ padding: '4px 10px', background: '#4ade80', color: '#000', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Save</button>
                    <button onClick={() => setEditingNote(null)} style={{ padding: '4px 10px', background: '#e5e7eb', color: '#333', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#92400e', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>📌 {n.Note}</div>
                  <div style={{ fontSize: 10, color: '#b45309', marginTop: 4 }}>
                    {n.DisplayName || n.CreatedBy.split('@')[0]} · {new Date(n.CreatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
                    <button onClick={() => setEditingNote(n.Id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 2 }} title="Edit">✏️</button>
                    <button onClick={async () => {
                      if (!window.confirm('Delete this pinned note?')) return;
                      await fetch(`/api/crm/pinned-notes/${n.Id}`, { method: 'DELETE' });
                      setPinnedNotes(prev => prev.filter(p => p.Id !== n.Id));
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 2 }} title="Delete">🗑️</button>
                  </div>
                </>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a pinned note..."
              onKeyDown={async e => {
                if (e.key === 'Enter' && newNote.trim()) {
                  const r = await fetch('/api/crm/pinned-notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companyCounter: parseInt(id), note: newNote.trim() }) }).then(r => r.json());
                  if (r.ok) {
                    setPinnedNotes(prev => [{ Id: r.id, CompanyCounter: parseInt(id), Note: newNote.trim(), CreatedBy: user.email, DisplayName: user.name, CreatedAt: new Date().toISOString() }, ...prev]);
                    setNewNote('');
                  }
                }
              }}
              style={{ flex: 1, background: 'rgba(254,243,199,0.3)', border: '1px solid rgba(252,211,77,0.4)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: C.text }} />
            <button onClick={async () => {
              if (!newNote.trim()) return;
              const r = await fetch('/api/crm/pinned-notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companyCounter: parseInt(id), note: newNote.trim() }) }).then(r => r.json());
              if (r.ok) {
                setPinnedNotes(prev => [{ Id: r.id, CompanyCounter: parseInt(id), Note: newNote.trim(), CreatedBy: user.email, DisplayName: user.name, CreatedAt: new Date().toISOString() }, ...prev]);
                setNewNote('');
              }
            }} style={{ padding: '8px 16px', background: '#fcd34d', color: '#92400e', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📌 Pin</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="crm-detail-tabs">
          {['overview', 'contacts', 'jobs', 'opportunities', 'activity'].map(t => (
            <button key={t} className={`crm-detail-tab ${tab === t ? 'active' : ''}`}
              onClick={() => setTab(t)}>
              {t === 'overview' ? 'Overview' : t === 'contacts' ? `Contacts (${contacts.length})` : t === 'jobs' ? `Jobs (${jobs.length})` : t === 'opportunities' ? `Opportunities (${clientOpps.length})` : 'Activity'}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {tab === 'overview' && editingClient ? (
          <div className="crm-detail-grid">
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Edit Company</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Company Name *</label>
                  <input className="crm-input" value={editClientData.companyName} onChange={e => setEditClientData({ ...editClientData, companyName: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Phone</label>
                  <input className="crm-input" value={editClientData.mainTelephone} onChange={e => setEditClientData({ ...editClientData, mainTelephone: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Email</label>
                  <input className="crm-input" value={editClientData.emailAddress} onChange={e => setEditClientData({ ...editClientData, emailAddress: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Mobile</label>
                  <input className="crm-input" value={editClientData.cellular} onChange={e => setEditClientData({ ...editClientData, cellular: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Fax</label>
                  <input className="crm-input" value={editClientData.mainFax} onChange={e => setEditClientData({ ...editClientData, mainFax: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Website</label>
                  <input className="crm-input" value={editClientData.url} onChange={e => setEditClientData({ ...editClientData, url: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">VAT Number</label>
                  <input className="crm-input" value={editClientData.vatNumber} onChange={e => setEditClientData({ ...editClientData, vatNumber: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Accounts Phone</label>
                  <input className="crm-input" value={editClientData.accountsTelephone} onChange={e => setEditClientData({ ...editClientData, accountsTelephone: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Accounts Email</label>
                  <input className="crm-input" value={editClientData.accountsEmail} onChange={e => setEditClientData({ ...editClientData, accountsEmail: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Address</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 1</label>
                  <input className="crm-input" value={editClientData.address1} onChange={e => setEditClientData({ ...editClientData, address1: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 2</label>
                  <input className="crm-input" value={editClientData.address2} onChange={e => setEditClientData({ ...editClientData, address2: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 3</label>
                  <input className="crm-input" value={editClientData.address3} onChange={e => setEditClientData({ ...editClientData, address3: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Town</label>
                  <input className="crm-input" value={editClientData.town} onChange={e => setEditClientData({ ...editClientData, town: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">County</label>
                  <input className="crm-input" value={editClientData.county} onChange={e => setEditClientData({ ...editClientData, county: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Postcode</label>
                  <input className="crm-input" value={editClientData.postCode} onChange={e => setEditClientData({ ...editClientData, postCode: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Country</label>
                  <input className="crm-input" value={editClientData.country} onChange={e => setEditClientData({ ...editClientData, country: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button className="crm-btn-primary" onClick={saveEditClient}>💾 Save & Push to HireTrack</button>
                <button onClick={() => setEditingClient(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          </div>
        ) : null}
        {tab === 'overview' && !editingClient && (
          <div className="crm-detail-grid">
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Contact Details</h3>
              <div className="crm-detail-fields">
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Phone</div>
                  <div className="crm-detail-field-value">{company.MainTelephone || '–'}</div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Email</div>
                  <div className="crm-detail-field-value" style={{ color: company.EmailAddress ? C.purpleLight : C.textDim }}>
                    {company.EmailAddress || '–'}
                  </div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Mobile</div>
                  <div className="crm-detail-field-value">{company.Cellular || '–'}</div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Fax</div>
                  <div className="crm-detail-field-value">{company.MainFax || '–'}</div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Website</div>
                  <div className="crm-detail-field-value" style={{ color: company.URL ? C.purpleLight : C.textDim }}>
                    {company.URL ? <a href={company.URL.startsWith('http') ? company.URL : `https://${company.URL}`}
                      target="_blank" rel="noreferrer" style={{ color: C.purpleLight }}>{company.URL}</a> : '–'}
                  </div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Accounts Phone</div>
                  <div className="crm-detail-field-value">{company.AccountsTelephone || '–'}</div>
                </div>
                <div className="crm-detail-field">
                  <div className="crm-detail-field-label">Accounts Email</div>
                  <div className="crm-detail-field-value">{company.AccountsEmail || '–'}</div>
                </div>
              </div>
            </div>
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Address</h3>
              <div className="crm-detail-address">
                {[company.Address1, company.Address2, company.Address3, company.Town, company.County, company.PostCode, company.Country]
                  .filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
              </div>
              {company.VatNumber && (
                <>
                  <h3 className="crm-detail-section-title" style={{ marginTop: 20 }}>Financial</h3>
                  <div className="crm-detail-fields">
                    <div className="crm-detail-field">
                      <div className="crm-detail-field-label">VAT Number</div>
                      <div className="crm-detail-field-value">{company.VatNumber}</div>
                    </div>
                    {company.AccountLimit > 0 && (
                      <div className="crm-detail-field">
                        <div className="crm-detail-field-label">Account Limit</div>
                        <div className="crm-detail-field-value">{fmtMoney(company.AccountLimit)}</div>
                      </div>
                    )}
                  </div>
                </>
              )}
              <ContactFrequency entityType="company" entityId={parseInt(id)} />
            </div>
            {/* HireTrack Notes */}
            {company.Notes && (
              <div className="crm-detail-section" style={{ gridColumn: '1 / -1' }}>
                <h3 className="crm-detail-section-title">📋 HireTrack Notes</h3>
                <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, background: '#0f1117', padding: 14, borderRadius: 8, border: `1px solid ${C.cardBorder}` }}>
                  {company.Notes}
                </div>
              </div>
            )}
            {/* Health Score */}
            {healthScore && (
              <div className="crm-detail-section" style={{ gridColumn: '1 / -1' }}>
                <h3 className="crm-detail-section-title">Client Health Score</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
                  <div style={{ position: 'relative', width: 80, height: 80 }}>
                    <svg width="80" height="80" viewBox="0 0 80 80">
                      <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                      <circle cx="40" cy="40" r="34" fill="none" stroke={healthScore.gradeColor} strokeWidth="8"
                        strokeDasharray={`${healthScore.score * 2.136} 213.6`} strokeLinecap="round"
                        transform="rotate(-90 40 40)" />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, color: healthScore.gradeColor }}>
                      {healthScore.score}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: healthScore.gradeColor }}>{healthScore.grade}</div>
                    <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>out of 100 possible points</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {healthScore.factors.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                      <div style={{ width: 40, textAlign: 'right', fontWeight: 700, color: f.color }}>{f.points > 0 ? `+${f.points}` : '0'}</div>
                      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${(f.points / 30) * 100}%`, height: '100%', background: f.color, borderRadius: 3 }} />
                      </div>
                      <div style={{ color: f.color, minWidth: 180 }}>{f.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONTACTS TAB */}
        {tab === 'contacts' && (
          <div className="crm-contacts-grid">
            {contacts.length === 0 ? (
              <p style={{ color: C.textDim, gridColumn: '1 / -1' }}>No contacts linked to this company.</p>
            ) : contacts.map(c => (
              <div key={c.NameCounter} className="crm-contact-card">
                <div className="crm-contact-header">
                  <div className="crm-contact-avatar" style={{ background: getColor(c.NameCounter) }}>
                    {initials(c.FORENAME, c.SURNAME)}
                  </div>
                  <div>
                    <div className="crm-contact-name">{c.FORENAME} {c.SURNAME}</div>
                    <div className="crm-contact-role">{c.TITLE || '–'}</div>
                  </div>
                </div>
                <div className="crm-contact-details">
                  <div>
                    <div className="crm-contact-label">Email</div>
                    <div className="crm-contact-value" style={{ color: c.EMAIL ? C.purpleLight : C.textDim }}>
                      {c.EMAIL || '–'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="crm-contact-label">Phone</div>
                    <div className="crm-contact-value">{c.Mobile || c.Telephone || '–'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* JOBS TAB */}
        {tab === 'jobs' && (
          <>
            <div style={{ marginBottom: 12 }}>
              <input type="text" placeholder="Search jobs..." value={jobSearch}
                onChange={e => setJobSearch(e.target.value)} className="crm-search-input" style={{ width: 280 }} />
            </div>
            <div className="crm-table-card">
              <div className="crm-table-wrap" style={{ maxHeight: 'calc(100vh - 340px)' }}>
                <table className="crm-table">
                  <thead>
                    <tr>
                      {[
                        { key: 'JobRef', label: 'JOB REF' },
                        { key: 'JobTitle', label: 'TITLE' },
                        { key: 'JobType', label: 'TYPE' },
                        { key: 'Status', label: 'STATUS' },
                        { key: 'DueOut', label: 'DUE OUT' },
                        { key: 'QuoteIncome', label: 'QUOTE', align: 'right' },
                        { key: 'ProvisionalIncome', label: 'PROV', align: 'right' },
                        { key: 'ConfirmedIncome', label: 'CONFIRMED', align: 'right' },
                        { key: 'InvoicedValue', label: 'INVOICED', align: 'right' },
                      ].map(col => (
                        <th key={col.key} className="crm-th" style={{ textAlign: col.align || 'left', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => toggleJobSort(col.key)}>
                          {col.label} {jobSortCol === col.key ? (jobSortDir === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedJobs.map((j, i) => (
                      <tr key={i} className="crm-tr">
                        <td className="crm-td" style={{ fontWeight: 600 }}>{j.JobRef}</td>
                        <td className="crm-td">{j.JobTitle}</td>
                        <td className="crm-td">{j.JobType || '–'}</td>
                        <td className="crm-td">
                          <span className={`crm-status-badge crm-status-${(j.Status || '').toLowerCase().replace(/\s/g, '-')}`}>
                            {j.Status}
                          </span>
                        </td>
                        <td className="crm-td">{j.DueOut}</td>
                        <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(j.QuoteIncome)}</td>
                        <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(j.ProvisionalIncome)}</td>
                        <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(j.ConfirmedIncome)}</td>
                        <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(j.InvoicedValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="crm-tr" style={{ fontWeight: 700, borderTop: `2px solid ${C.cardBorder}` }}>
                      <td className="crm-td" colSpan={5} style={{ textAlign: 'right' }}>Totals</td>
                      <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(jobTotals.quote)}</td>
                      <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(jobTotals.prov)}</td>
                      <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(jobTotals.conf)}</td>
                      <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(jobTotals.inv)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {/* OPPORTUNITIES TAB */}
        {tab === 'opportunities' && (
          <div className="crm-table-card">
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: C.textMuted }}>{clientOpps.length} opportunities</span>
              <button className="crm-btn-primary" style={{ padding: '6px 14px', fontSize: 12 }}
                onClick={() => navigate('/crm/opportunities')}>
                + New Opportunity
              </button>
            </div>
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th className="crm-th" style={{ textAlign: 'left' }}>TITLE</th>
                    <th className="crm-th" style={{ textAlign: 'center' }}>STATUS</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>VALUE</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>ASSIGNED</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>UPDATED</th>
                  </tr>
                </thead>
                <tbody>
                  {clientOpps.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: C.textDim, padding: 30 }}>
                      No opportunities linked to this client
                    </td></tr>
                  )}
                  {clientOpps.map(o => (
                    <tr key={o.Id} className="crm-tr" style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/crm/opportunities/${o.Id}`)}>
                      <td className="crm-td" style={{ fontWeight: 600 }}>{o.Title}</td>
                      <td className="crm-td" style={{ textAlign: 'center' }}>
                        <span className="crm-status-badge" style={{ background: o.StatusColor, color: '#fff' }}>{o.StatusName}</span>
                      </td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(o.EstimatedValue)}</td>
                      <td className="crm-td"><span className="crm-assignee-tag">{(o.AssignedName || '').split(' ')[0]}</span></td>
                      <td className="crm-td" style={{ fontSize: 12, color: C.textDim }}>
                        {new Date(o.UpdatedAt).toLocaleDateString('en-GB')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'activity' && (
          <div className="crm-table-card" style={{ padding: 20 }}>
            <ActivityLog entityType="company" entityId={parseInt(id)} />
          </div>
        )}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// MY CONTACTS PAGE
// ══════════════════════════════════════════════

const CrmContactDetail = ({ user, onMenuToggle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTypes, setSubTypes] = useState([]);
  const [subs, setSubs] = useState({});
  const [contactOpps, setContactOpps] = useState([]);
  const [editingJobTitle, setEditingJobTitle] = useState(false);
  const [jobTitleValue, setJobTitleValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const saveJobTitle = async () => {
    await fetch(`/api/crm/contact/${id}/job-title`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobTitle: jobTitleValue.trim() }),
    });
    setData(prev => {
      const updated = { ...prev };
      updated.contact = { ...updated.contact, JobTitle: jobTitleValue.trim() };
      return updated;
    });
    setEditingJobTitle(false);
  };

  const startEdit = () => {
    setEditing(true);
    setEditData({
      forename: c.FORENAME || '', surname: c.SURNAME || '', title: c.TITLE || '',
      email: c.EMAIL || '', mobile: c.Mobile || '', telephone: c.Telephone || '',
      salutation: c.Salutation || '', nickname: c.Nickname || '', jobTitle: c.JobTitle || '',
      address1: c.Address1 || '', address2: c.Address2 || '', address3: c.Address3 || '',
      town: c.Town || '', county: c.County || '', postcode: c.Postcode || '', country: c.Country || '',
    });
  };

  const saveEdit = async () => {
    const res = await fetch(`/api/crm/contact/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editData),
    });
    const result = await res.json();
    if (res.ok) {
      // Refresh the page data
      const detail = await fetch(`/api/crm/contact/${id}`).then(r => r.ok ? r.json() : null);
      if (detail) {
        setData(detail);
        const subMap = {};
        detail.subscriptions.forEach(s => { subMap[s.SubscriptionTypeId] = true; });
        setSubs(subMap);
      }
      setEditing(false);
      if (result.nexusPushed === false) {
        alert('Saved to CRM but could not push to HireTrack — it will sync on next scheduled sync.');
      }
    } else {
      alert(result.error || 'Failed to save');
    }
  };

  useEffect(() => {
    Promise.all([
      fetch(`/api/crm/contact/${id}`).then(r => r.ok ? r.json() : null),
      fetch('/api/crm/subscription-types').then(r => r.ok ? r.json() : []),
      fetch(`/api/crm/opportunities?contactId=${id}`).then(r => r.ok ? r.json() : []),
    ]).then(([detail, types, opps]) => {
      if (detail) {
        setData(detail);
        const subMap = {};
        detail.subscriptions.forEach(s => { subMap[s.SubscriptionTypeId] = true; });
        setSubs(subMap);
      }
      setSubTypes(types);
      setContactOpps(opps || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const toggleSub = (subTypeId) => {
    const current = subs[subTypeId] || false;
    setSubs(prev => ({ ...prev, [subTypeId]: !current }));
    fetch('/api/crm/contact-subscription', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nameCounter: parseInt(id), subscriptionTypeId: subTypeId, isSubscribed: !current }),
    });
  };

  if (loading) return <Loading />;
  if (!data) return (
    <div className="crm-content">
      <p style={{ color: C.textDim }}>Contact not found.</p>
      <Link to="/crm/contacts" style={{ color: C.purple }}>← Back to contacts</Link>
    </div>
  );

  const c = data.contact;
  const avatarColors = [C.purple, C.blue, C.green, C.orange, C.pink, C.cyan];
  const color = avatarColors[c.NameCounter % avatarColors.length];
  const initials = ((c.FORENAME || '').charAt(0) + (c.SURNAME || '').charAt(0)).toUpperCase() || '?';

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <button onClick={() => navigate('/crm/contacts')} className="crm-back-btn">← Contacts</button>
      </CrmTopBar>
      <div className="crm-content">
        <div className="crm-detail-header">
          <div className="crm-detail-avatar" style={{ background: color, borderRadius: '50%' }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <h2 className="crm-detail-name">{c.FORENAME} {c.SURNAME}</h2>
            <div className="crm-detail-meta">
              {c.TITLE && <span>{c.TITLE}</span>}
              {c.JobTitle && <span>{c.JobTitle}</span>}
              {c.CompanyName && (
                <span style={{ cursor: 'pointer', color: C.purpleLight }}
                  onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                  {c.CompanyName}
                </span>
              )}
              {c.Nickname && <span>"{c.Nickname}"</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            {!editing && (
              <button className="crm-btn-primary" onClick={startEdit}
                style={{ fontSize: 12, padding: '6px 14px' }}>✏️ Edit</button>
            )}
          </div>
          <div className="crm-detail-subs">
            {subTypes.map(st => (
              <SubTag key={st.Id} label={st.Name} active={subs[st.Id] || false} color={st.Color}
                onClick={() => toggleSub(st.Id)} />
            ))}
          </div>
        </div>

        {editing ? (
          <div className="crm-detail-grid">
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Edit Contact</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="crm-field-label">First Name</label>
                  <input className="crm-input" value={editData.forename} onChange={e => setEditData({ ...editData, forename: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Surname *</label>
                  <input className="crm-input" value={editData.surname} onChange={e => setEditData({ ...editData, surname: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Title (Mr/Mrs/etc)</label>
                  <input className="crm-input" value={editData.title} onChange={e => setEditData({ ...editData, title: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Salutation</label>
                  <input className="crm-input" value={editData.salutation} onChange={e => setEditData({ ...editData, salutation: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Job Title</label>
                  <input className="crm-input" value={editData.jobTitle} onChange={e => setEditData({ ...editData, jobTitle: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Nickname</label>
                  <input className="crm-input" value={editData.nickname} onChange={e => setEditData({ ...editData, nickname: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Email</label>
                  <input className="crm-input" value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Phone</label>
                  <input className="crm-input" value={editData.telephone} onChange={e => setEditData({ ...editData, telephone: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Mobile</label>
                  <input className="crm-input" value={editData.mobile} onChange={e => setEditData({ ...editData, mobile: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="crm-detail-section">
              <h3 className="crm-detail-section-title">Address</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 1</label>
                  <input className="crm-input" value={editData.address1} onChange={e => setEditData({ ...editData, address1: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 2</label>
                  <input className="crm-input" value={editData.address2} onChange={e => setEditData({ ...editData, address2: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="crm-field-label">Address Line 3</label>
                  <input className="crm-input" value={editData.address3} onChange={e => setEditData({ ...editData, address3: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Town</label>
                  <input className="crm-input" value={editData.town} onChange={e => setEditData({ ...editData, town: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">County</label>
                  <input className="crm-input" value={editData.county} onChange={e => setEditData({ ...editData, county: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Postcode</label>
                  <input className="crm-input" value={editData.postcode} onChange={e => setEditData({ ...editData, postcode: e.target.value })} />
                </div>
                <div>
                  <label className="crm-field-label">Country</label>
                  <input className="crm-input" value={editData.country} onChange={e => setEditData({ ...editData, country: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button className="crm-btn-primary" onClick={saveEdit}>💾 Save & Push to HireTrack</button>
                <button onClick={() => setEditing(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          </div>
        ) : (
        <div className="crm-detail-grid">
          <div className="crm-detail-section">
            <h3 className="crm-detail-section-title">Contact Details</h3>
            <div className="crm-detail-fields">
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Job Title</div>
                <div className="crm-detail-field-value">
                  {editingJobTitle ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="text" value={jobTitleValue} onChange={e => setJobTitleValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveJobTitle(); if (e.key === 'Escape') setEditingJobTitle(false); }}
                        className="crm-input" style={{ padding: '4px 8px', fontSize: 13 }} autoFocus />
                      <button onClick={saveJobTitle} style={{ background: 'none', border: 'none', color: '#4ade80', cursor: 'pointer', fontSize: 16 }}>✓</button>
                      <button onClick={() => setEditingJobTitle(false)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                  ) : (
                    <span style={{ cursor: 'pointer' }} onClick={() => { setJobTitleValue(c.JobTitle || ''); setEditingJobTitle(true); }}>
                      {c.JobTitle || <span style={{ color: C.textDim }}>+ Add job title</span>}
                    </span>
                  )}
                </div>
              </div>
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Email</div>
                <div className="crm-detail-field-value" style={{ color: c.EMAIL ? C.purpleLight : C.textDim }}>
                  {c.EMAIL || '–'}
                </div>
              </div>
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Phone</div>
                <div className="crm-detail-field-value">{c.Telephone || '–'}</div>
              </div>
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Mobile</div>
                <div className="crm-detail-field-value">{c.Mobile || '–'}</div>
              </div>
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Fax</div>
                <div className="crm-detail-field-value">{c.Facsimile || '–'}</div>
              </div>
              <div className="crm-detail-field">
                <div className="crm-detail-field-label">Salutation</div>
                <div className="crm-detail-field-value">{c.Salutation || '–'}</div>
              </div>
            </div>
          </div>
          <div className="crm-detail-section">
            <h3 className="crm-detail-section-title">Address</h3>
            <div className="crm-detail-address">
              {[c.Address1, c.Address2, c.Address3, c.Town, c.County, c.Postcode, c.Country]
                .filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
              {![c.Address1, c.Address2, c.Address3, c.Town].some(Boolean) && (
                <span style={{ color: C.textDim }}>No address on file</span>
              )}
            </div>
            {c.CompanyName && (
              <>
                <h3 className="crm-detail-section-title" style={{ marginTop: 20 }}>Company</h3>
                <div style={{ cursor: 'pointer', color: C.purpleLight, fontSize: 14, fontWeight: 600 }}
                  onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                  {c.CompanyName} →
                </div>
              </>
            )}
            <ContactFrequency entityType="contact" entityId={parseInt(id)} />
          </div>
        </div>
        )}

        {/* Opportunities linked to this contact */}
        {contactOpps.length > 0 && (
          <div className="crm-detail-section" style={{ marginTop: 16 }}>
            <h3 className="crm-detail-section-title">Opportunities ({contactOpps.length})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {contactOpps.map(o => (
                <div key={o.Id} className="crm-dash-list-item" style={{ cursor: 'pointer', padding: '8px 12px', background: '#0f1117', borderRadius: 8, border: '1px solid #2a2d3a' }}
                  onClick={() => navigate(`/crm/opportunities/${o.Id}`)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{o.Title}</div>
                    <div style={{ fontSize: 11, color: C.textDim }}>{o.CompanyName || ''}</div>
                  </div>
                  <span className="crm-status-badge" style={{ background: o.StatusColor, color: '#fff' }}>{o.StatusName}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.green, marginLeft: 8 }}>{fmtMoney(o.EstimatedValue)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Log */}
        <div className="crm-detail-section" style={{ marginTop: 16 }}>
          <ActivityLog entityType="contact" entityId={parseInt(id)} companyCounter={c.CompanyCounter} />
        </div>
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// ACTIVITY LOG COMPONENT (reusable)
// ══════════════════════════════════════════════

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'meeting', label: 'Meeting', icon: '🤝' },
  { value: 'note', label: 'Note', icon: '📝' },
  { value: 'site_visit', label: 'Site Visit', icon: '🏢' },
  { value: 'quote', label: 'Quote Sent', icon: '💷' },
  { value: 'task', label: 'Task Completed', icon: '✅', auto: true },
];

const ActivityLog = ({ entityType, entityId, companyCounter, onActivityLogged }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [actType, setActType] = useState('call');
  const [subject, setSubject] = useState('');
  const [desc, setDesc] = useState('');
  const [actDate, setActDate] = useState(new Date().toISOString().split('T')[0]);
  const [setReminder, setSetReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [viewEmail, setViewEmail] = useState(null);

  const fetchActivities = useCallback(() => {
    fetch(`/api/crm/activities/${entityType}/${entityId}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { setActivities(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const submit = async () => {
    if (!subject.trim()) return;
    await fetch('/api/crm/activities', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityType, entityId, activityType: actType, subject, description: desc, activityDate: actDate }),
    });
    // Create follow-up task if reminder is set
    if (setReminder && reminderDate) {
      const typeLabel = ACTIVITY_TYPES.find(t => t.value === actType)?.label || actType;
      await fetch('/api/crm/tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Follow up: ${subject.trim()}`,
          description: `Reminder set from ${typeLabel} logged on ${actDate}${desc ? '. Notes: ' + desc : ''}`,
          taskType: 'manual', priority: 'normal',
          dueDate: reminderDate,
          companyCounter: entityType === 'company' ? entityId : (companyCounter || null),
          contactId: entityType === 'contact' ? entityId : null,
        }),
      });
    }
    setSubject(''); setDesc(''); setShowForm(false);
    setActDate(new Date().toISOString().split('T')[0]);
    setSetReminder(false); setReminderDate('');
    fetchActivities();
    if (onActivityLogged) onActivityLogged();
  };

  const deleteActivity = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    await fetch(`/api/crm/activities/${id}`, { method: 'DELETE' });
    fetchActivities();
  };

  const getIcon = (type) => (ACTIVITY_TYPES.find(t => t.value === type)?.icon) || '📋';
  const getLabel = (type) => (ACTIVITY_TYPES.find(t => t.value === type)?.label) || type;

  const ActivityDesc = ({ text, isLong }) => {
    const [expanded, setExpanded] = useState(false);
    const display = isLong && !expanded ? text.slice(0, 200) + '...' : text;
    return (
      <div style={{ fontSize: 12, color: C.textDim, marginTop: 4, lineHeight: 1.4 }}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{display}</span>
        {isLong && (
          <button onClick={() => setExpanded(!expanded)}
            style={{ background: 'none', border: 'none', color: C.purpleLight, cursor: 'pointer', fontSize: 11, marginLeft: 4, padding: 0 }}>
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const now = new Date();
    const diff = Math.floor((now - dt) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const people = [...new Map(activities.map(a => [a.CreatedBy, a.CreatedByName || a.CreatedBy?.split('@')[0]])).entries()];
  const filtered = filterBy === 'all' ? activities : activities.filter(a => a.CreatedBy === filterBy);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 className="crm-detail-section-title" style={{ margin: 0 }}>Activity Log ({filtered.length}{filterBy !== 'all' ? `/${activities.length}` : ''})</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {people.length > 1 && (
            <select className="crm-input" value={filterBy} onChange={e => setFilterBy(e.target.value)}
              style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
              <option value="all">All people</option>
              {people.map(([email, name]) => <option key={email} value={email}>{name}</option>)}
            </select>
          )}
          <button className="crm-btn-primary" style={{ padding: '5px 12px', fontSize: 12 }}
            onClick={() => setShowForm(!showForm)}>+ Log Activity</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="crm-field-label">Type</label>
              <select className="crm-input" value={actType} onChange={e => setActType(e.target.value)}>
                {ACTIVITY_TYPES.filter(t => !t.auto).map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="crm-field-label">Date</label>
              <input type="date" className="crm-input" value={actDate} onChange={e => setActDate(e.target.value)} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="crm-field-label">Subject *</label>
              <input className="crm-input" value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Discussed Q2 hire requirements" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="crm-field-label">Notes</label>
              <textarea className="crm-input" rows={2} value={desc} onChange={e => setDesc(e.target.value)}
                placeholder="Optional details..." />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: C.text }}>
                <input type="checkbox" checked={setReminder} onChange={e => setSetReminder(e.target.checked)}
                  style={{ accentColor: C.purple }} />
                Set follow-up reminder
              </label>
              {setReminder && (
                <input type="date" className="crm-input" value={reminderDate} onChange={e => setReminderDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: 160, padding: '4px 8px', fontSize: 12 }} />
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="crm-btn-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={submit}>Save Activity</button>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: C.textDim, fontSize: 13 }}>Loading...</div> : (
        <div style={{ position: 'relative' }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: C.textDim, fontSize: 13 }}>
              {filterBy !== 'all' ? 'No activities from this person' : 'No activities logged yet'}
            </div>
          )}
          {/* Timeline */}
          {filtered.length > 0 && (
            <div style={{ borderLeft: `2px solid ${C.cardBorder}`, marginLeft: 14, paddingLeft: 20 }}>
              {filtered.map((a, i) => (
                <div key={a.Id} style={{ position: 'relative', paddingBottom: i < filtered.length - 1 ? 16 : 0 }}>
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute', left: -27, top: 4, width: 12, height: 12, borderRadius: '50%',
                    background: C.bg, border: `2px solid ${C.purple}`, zIndex: 1,
                  }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14 }}>{getIcon(a.ActivityType)}</span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{a.Subject}</span>
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 4,
                          background: 'rgba(124,58,237,0.15)', color: '#a78bfa',
                        }}>{getLabel(a.ActivityType)}</span>
                      </div>
                      {a.Description && (() => {
                        const cleaned = a.Description.replace(/\n?Gmail:[a-zA-Z0-9]+$/m, '').trim();
                        if (!cleaned) return null;
                        if (a.ActivityType === 'email') {
                          return (
                            <button onClick={() => setViewEmail(a)}
                              style={{ background: 'none', border: 'none', color: C.purpleLight, cursor: 'pointer', fontSize: 12, padding: 0, marginTop: 4 }}>
                              📧 View email
                            </button>
                          );
                        }
                        const isLong = cleaned.length > 200;
                        return <ActivityDesc key={a.Id} text={cleaned} isLong={isLong} />;
                      })()}
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                        {fmtDate(a.ActivityDate)} · {a.CreatedByName || a.CreatedBy?.split('@')[0]}
                      </div>
                    </div>
                    <button onClick={() => deleteActivity(a.Id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 12, padding: '2px 6px', opacity: 0.5 }}
                      title="Delete">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Email Viewer Modal */}
      {viewEmail && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setViewEmail(null)}>
          <div style={{
            background: '#1e1e2e', borderRadius: 12, width: '100%', maxWidth: 850,
            maxHeight: '85vh', height: '80vh', display: 'flex', flexDirection: 'column',
            border: `1px solid ${C.cardBorder}`, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: `1px solid ${C.cardBorder}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 8 }}>
                  {viewEmail.Subject}
                </div>
                <div style={{ fontSize: 12, color: C.textDim, lineHeight: 1.8 }}>
                  {viewEmail.EmailFrom && <div><span style={{ color: C.textMuted, width: 40, display: 'inline-block' }}>From:</span> <strong style={{ color: C.text }}>{viewEmail.EmailFrom}</strong></div>}
                  {viewEmail.EmailTo && <div><span style={{ color: C.textMuted, width: 40, display: 'inline-block' }}>To:</span> {viewEmail.EmailTo}</div>}
                  {viewEmail.EmailCc && <div><span style={{ color: C.textMuted, width: 40, display: 'inline-block' }}>Cc:</span> {viewEmail.EmailCc}</div>}
                  <div><span style={{ color: C.textMuted, width: 40, display: 'inline-block' }}>Date:</span> {new Date(viewEmail.ActivityDate).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
              <button onClick={() => setViewEmail(null)}
                style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 20, padding: '0 4px', lineHeight: 1 }}>✕</button>
            </div>
            {/* Body */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 200 }}>
              {viewEmail.HtmlBody ? (
                <iframe
                  title="Email content"
                  srcDoc={viewEmail.HtmlBody}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#fff', borderRadius: '0 0 12px 12px' }}
                  sandbox="allow-same-origin"
                />
              ) : (
                <div style={{
                  padding: 20, overflowY: 'auto', height: '100%',
                  fontSize: 13, color: '#ddd', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {(viewEmail.Description || '').replace(/\n?Gmail:[a-zA-Z0-9]+$/m, '').trim() || 'No content'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════

const SUB_DEFAULT_COLORS = ['#7c3aed', '#38bdf8', '#4ade80', '#fb923c', '#f472b6', '#facc15', '#ef4444', '#06b6d4', '#a78bfa', '#34d399'];

const SubTag = ({ label, active, onClick, color }) => {
  const c = color || C.purple;
  return (
    <span className={`crm-sub-tag ${active ? 'active' : ''}`}
      style={active ? { background: c, borderColor: c } : {}}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}>
      {label}
    </span>
  );
};

const FREQ_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 7, label: 'Weekly' },
  { value: 14, label: 'Fortnightly' },
  { value: 30, label: 'Monthly' },
  { value: 60, label: 'Every 2 Months' },
  { value: 90, label: 'Quarterly' },
  { value: 180, label: 'Every 6 Months' },
  { value: 365, label: 'Yearly' },
];

const ContactFrequency = ({ entityType, entityId }) => {
  const [freq, setFreq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/crm/contact-frequency/${entityType}/${entityId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setFreq(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entityType, entityId]);

  const setFrequency = async (days) => {
    await fetch('/api/crm/contact-frequency', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityType, entityId, frequencyDays: parseInt(days) }),
    });
    if (parseInt(days) === 0) {
      setFreq(null);
    } else {
      const r = await fetch(`/api/crm/contact-frequency/${entityType}/${entityId}`);
      if (r.ok) setFreq(await r.json());
    }
  };

  const markContacted = async () => {
    await fetch('/api/crm/contact-frequency/touch', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityType, entityId }),
    });
    const r = await fetch(`/api/crm/contact-frequency/${entityType}/${entityId}`);
    if (r.ok) setFreq(await r.json());
  };

  if (loading) return null;

  const daysUntil = freq?.NextDueDate ? Math.ceil((new Date(freq.NextDueDate) - new Date()) / 86400000) : null;
  const isOverdue = daysUntil !== null && daysUntil < 0;
  const isDueSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 3;

  return (
    <div className="crm-freq-widget">
      <h3 className="crm-detail-section-title">Contact Frequency</h3>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="crm-input" style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
          value={freq?.FrequencyDays || 0} onChange={e => setFrequency(e.target.value)}>
          {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {freq && (
          <>
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
              background: isOverdue ? '#ef444420' : isDueSoon ? '#fb923c20' : '#1e2130',
              color: isOverdue ? '#ef4444' : isDueSoon ? '#fb923c' : C.textMuted,
            }}>
              {isOverdue ? `Overdue by ${Math.abs(daysUntil)}d` :
               daysUntil === 0 ? 'Due today' :
               `Due in ${daysUntil}d`}
            </span>
            <button className="crm-btn-small" onClick={markContacted}>
              ✓ Contacted
            </button>
          </>
        )}
      </div>
      {freq && (
        <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
          Last: {freq.LastContacted ? new Date(freq.LastContacted).toLocaleDateString('en-GB') : 'Not set'}
          {freq.NextDueDate && ` · Next: ${new Date(freq.NextDueDate).toLocaleDateString('en-GB')}`}
        </div>
      )}
    </div>
  );
};

const CrmContacts = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newContact, setNewContact] = useState({ forename: '', surname: '', email: '', mobile: '', telephone: '', title: '' });
  const [newCompanySearch, setNewCompanySearch] = useState('');
  const [newCompanySuggestions, setNewCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    Promise.all([
      fetch('/api/crm/contacts').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/subscription-types').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/contact-subscriptions').then(r => r.ok ? r.json() : []),
    ]).then(([contactData, types, subs]) => {
      setContacts(contactData);
      setSubTypes(types);
      const subMap = {};
      subs.forEach(s => {
        if (!subMap[s.NameCounter]) subMap[s.NameCounter] = {};
        subMap[s.NameCounter][s.SubscriptionTypeId] = s.IsSubscribed;
      });
      setSubscriptions(subMap);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleSub = (nameCounter, subTypeId) => {
    const current = subscriptions[nameCounter]?.[subTypeId] || false;
    setSubscriptions(prev => ({
      ...prev,
      [nameCounter]: { ...prev[nameCounter], [subTypeId]: !current }
    }));
    fetch('/api/crm/contact-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nameCounter, subscriptionTypeId: subTypeId, isSubscribed: !current }),
    });
  };

  const filtered = contacts.filter(c => {
    if (search) {
      const s = search.toLowerCase();
      if (!(c.FullName || '').toLowerCase().includes(s)
        && !(c.EMAIL || '').toLowerCase().includes(s)
        && !(c.CompanyName || '').toLowerCase().includes(s)
        && !(c.JobTitle || '').toLowerCase().includes(s)
        && !(c.Mobile || '').toLowerCase().includes(s)
        && !(c.Telephone || '').toLowerCase().includes(s)) return false;
    }
    if (filterSub) {
      const subId = parseInt(filterSub);
      if (!subscriptions[c.NameCounter]?.[subId]) return false;
    }
    return true;
  });

  const initials = (f, s) => ((f || '').charAt(0) + (s || '').charAt(0)).toUpperCase() || '?';

  const avatarColors = [C.purple, C.blue, C.green, C.orange, C.pink, C.cyan];
  const getColor = (id) => avatarColors[id % avatarColors.length];

  const searchCompanyForContact = (q) => {
    setNewCompanySearch(q);
    setSelectedCompany(null);
    if (q.length < 2) { setNewCompanySuggestions([]); return; }
    clearTimeout(window._contactCompTimer);
    window._contactCompTimer = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(q)}`).then(r => r.ok ? r.json() : {})
        .then(d => setNewCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
  };

  const [bulkAction, setBulkAction] = useState('');
  const toggleSelectContact = (nc, e) => { e.stopPropagation(); setSelected(prev => { const s = new Set(prev); s.has(nc) ? s.delete(nc) : s.add(nc); return s; }); };
  const selectAllContacts = () => {
    const visible = filtered.slice(0, 100).map(c => c.NameCounter);
    setSelected(prev => prev.size === visible.length ? new Set() : new Set(visible));
  };
  const bulkSubscribe = async (subTypeId, isSub) => {
    await fetch('/api/crm/contact-subscription-bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nameCounters: [...selected], subscriptionTypeId: subTypeId, isSubscribed: isSub }),
    });
    setSubscriptions(prev => {
      const next = { ...prev };
      for (const nc of selected) { next[nc] = { ...next[nc], [subTypeId]: isSub }; }
      return next;
    });
    alert(`${isSub ? 'Added' : 'Removed'} tag ${isSub ? 'to' : 'from'} ${selected.size} contacts`);
    setSelected(new Set()); setBulkAction('');
  };

  const createContact = async () => {
    if (!newContact.forename.trim() || !newContact.surname.trim()) return;
    const body = { ...newContact, companyCounter: selectedCompany?.CompanyCounter || null };
    const res = await fetch('/api/crm/create-contact', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.ok) {
      setShowCreate(false);
      setNewContact({ forename: '', surname: '', email: '', mobile: '', telephone: '', title: '' });
      setNewCompanySearch(''); setSelectedCompany(null);
      if (data.nameCounter) {
        navigate(`/crm/contacts/${data.nameCounter}`);
      } else {
        alert(`Contact "${newContact.forename} ${newContact.surname}" created.`);
      }
    } else {
      alert(data.error || 'Failed to create');
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <input type="text" placeholder="Search contacts..." value={search}
          onChange={e => setSearch(e.target.value)} className="crm-search-input" />
      </CrmTopBar>
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">My Contacts</h2>
            <p className="crm-page-subtitle">{filtered.length} contacts{search ? ` matching "${search}"` : ''}{filterSub ? ` · filtered by ${subTypes.find(s => s.Id === parseInt(filterSub))?.Name || ''}` : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canAccess(user, 'manager') && <button className="crm-btn-primary" onClick={() => {
              exportCSV(`contacts-${new Date().toISOString().split('T')[0]}.csv`,
                ['First Name', 'Last Name', 'Title', 'Job Title', 'Company', 'Email', 'Mobile', 'Phone'],
                filtered.map(c => [c.FORENAME, c.SURNAME, c.TITLE, c.JobTitle, c.CompanyName, c.EMAIL, c.Mobile, c.Telephone])
              );
            }} style={{ fontSize: 12, padding: '6px 14px', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}>
              ↓ Export CSV
            </button>}
            <button className="crm-btn-primary" onClick={() => setShowCreate(!showCreate)} style={{ fontSize: 12, padding: '6px 14px' }}>
              {showCreate ? '✕ Cancel' : '+ New Contact'}
            </button>
          </div>
        </div>

        {/* Create contact form */}
        {showCreate && (
          <div className="crm-create-card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New Contact</h3>
            <p style={{ fontSize: 12, color: C.textDim, marginBottom: 12 }}>Creates the contact in HireTrack and the CRM.</p>
            <div className="crm-create-grid">
              <div className="crm-create-field">
                <label>First Name *</label>
                <input type="text" value={newContact.forename} onChange={e => setNewContact({ ...newContact, forename: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Last Name *</label>
                <input type="text" value={newContact.surname} onChange={e => setNewContact({ ...newContact, surname: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Company</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={newCompanySearch} onChange={e => searchCompanyForContact(e.target.value)}
                    placeholder="Search company..." className="crm-input" />
                  {newCompanySuggestions.length > 0 && (
                    <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                      {newCompanySuggestions.map(c => (
                        <div key={c.CompanyCounter} className="crm-search-item" onClick={() => {
                          setSelectedCompany(c); setNewCompanySearch(c.CompanyName); setNewCompanySuggestions([]);
                        }}>
                          <span className="crm-search-item-title">{c.CompanyName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="crm-create-field">
                <label>Title</label>
                <input type="text" value={newContact.title} onChange={e => setNewContact({ ...newContact, title: e.target.value })} placeholder="e.g. Production Manager" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Email</label>
                <input type="email" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Mobile</label>
                <input type="text" value={newContact.mobile} onChange={e => setNewContact({ ...newContact, mobile: e.target.value })} className="crm-input" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <button className="crm-btn-primary" onClick={createContact}
                  disabled={!newContact.forename.trim() || !newContact.surname.trim()}
                  style={{ opacity: (newContact.forename.trim() && newContact.surname.trim()) ? 1 : 0.4 }}>
                  Create Contact
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription filters */}
        {subTypes.length > 0 && (
          <div className="crm-sub-filters">
            <span style={{ fontSize: 12, color: C.textDim, marginRight: 4 }}>Filter:</span>
            {subTypes.map(st => (
              <button key={st.Id} className={`crm-sub-filter-btn ${filterSub === String(st.Id) ? 'active' : ''}`}
                style={filterSub === String(st.Id) ? { background: st.Color || C.purple, borderColor: st.Color || C.purple, color: '#fff' } : {}}
                onClick={() => setFilterSub(filterSub === String(st.Id) ? '' : String(st.Id))}>
                {st.Name}
              </button>
            ))}
            {filterSub && (
              <button className="crm-sub-filter-clear" onClick={() => setFilterSub('')}>✕ Clear</button>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {canAccess(user, 'manager') && selected.size > 0 && (
          <div style={{
            background: C.purple, borderRadius: 10, padding: '12px 20px', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{selected.size} selected</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, background: '#2a1f4e', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', fontSize: 12 }}>
                <option value="" style={{ background: '#2a1f4e', color: '#fff' }}>Choose action...</option>
                <option value="subscribe" style={{ background: '#2a1f4e', color: '#fff' }}>Add tag...</option>
                <option value="unsubscribe" style={{ background: '#2a1f4e', color: '#fff' }}>Remove tag...</option>
              </select>
              {(bulkAction === 'subscribe' || bulkAction === 'unsubscribe') && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {subTypes.map(st => (
                    <button key={st.Id} onClick={() => bulkSubscribe(st.Id, bulkAction === 'subscribe')}
                      style={{ background: st.Color || 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                      {st.Name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => { setSelected(new Set()); setBulkAction(''); }}
              style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', cursor: 'pointer', borderRadius: 6, padding: '4px 12px', fontSize: 12 }}>
              ✕ Clear
            </button>
          </div>
        )}

        {canAccess(user, 'manager') && filtered.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: C.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={selected.size > 0 && selected.size === filtered.slice(0, 100).length}
                onChange={selectAllContacts} style={{ accentColor: C.purple }} />
              Select all ({Math.min(filtered.length, 100)})
            </label>
          </div>
        )}

        <div className="crm-contacts-grid">
          {filtered.slice(0, 100).map(c => (
            <div key={c.NameCounter} className="crm-contact-card" style={{ cursor: 'pointer', outline: selected.has(c.NameCounter) ? `2px solid ${C.purple}` : 'none' }}
              onClick={() => navigate(`/crm/contacts/${c.NameCounter}`)}>
              {canAccess(user, 'manager') && (
                <div style={{ position: 'absolute', top: 8, right: 8 }} onClick={e => toggleSelectContact(c.NameCounter, e)}>
                  <input type="checkbox" checked={selected.has(c.NameCounter)} readOnly
                    style={{ cursor: 'pointer', accentColor: C.purple }} />
                </div>
              )}
              <div className="crm-contact-header">
                <div className="crm-contact-avatar" style={{ background: getColor(c.NameCounter) }}>
                  {initials(c.FORENAME, c.SURNAME)}
                </div>
                <div>
                  <div className="crm-contact-name">{c.FORENAME} {c.SURNAME}</div>
                  <div className="crm-contact-role">
                    {c.TITLE ? `${c.TITLE} · ` : ''}{c.JobTitle ? `${c.JobTitle} · ` : ''}{c.CompanyName || 'No company'}
                  </div>
                </div>
              </div>
              <div className="crm-contact-details">
                <div>
                  <div className="crm-contact-label">Email</div>
                  <div className="crm-contact-value" style={{ color: c.EMAIL ? C.purpleLight : C.textDim }}>
                    {c.EMAIL || '–'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="crm-contact-label">Phone</div>
                  <div className="crm-contact-value">{c.Mobile || c.Telephone || '–'}</div>
                </div>
              </div>
              <div className="crm-contact-subs">
                {subTypes.map(st => (
                  <SubTag key={st.Id} label={st.Name}
                    active={subscriptions[c.NameCounter]?.[st.Id] || false}
                    color={st.Color}
                    onClick={() => toggleSub(c.NameCounter, st.Id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        {filtered.length > 100 && (
          <div className="crm-table-footer">
            Showing 100 of {filtered.length}. Use search to filter.
          </div>
        )}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// CRM DASHBOARD
// ══════════════════════════════════════════════

const CrmDashboard = ({ user, onMenuToggle }) => {
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dueContacts, setDueContacts] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [winLoss, setWinLoss] = useState(null);
  const [stalePipeline, setStalePipeline] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      fetch('/api/crm/dashboard').then(r => r.ok ? r.json() : null),
      fetch('/api/crm/pipeline-summary').then(r => r.ok ? r.json() : {}),
      fetch('/api/crm/contact-frequency-due').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/leaderboard').then(r => r.ok ? r.json() : null),
      fetch('/api/crm/win-loss').then(r => r.ok ? r.json() : null),
      fetch('/api/crm/stale-pipeline').then(r => r.ok ? r.json() : []),
    ]).then(([d, p, due, lb, wl, stale]) => {
      setData(d);
      setPipeline(p);
      setDueContacts(due.filter(c => {
        const days = c.DaysUntilDue;
        return days !== null && days <= 7;
      }));
      setLeaderboard(lb);
      setWinLoss(wl);
      setStalePipeline(stale || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const d = data || {};
  const p = pipeline || {};
  const pipelineTotal = (d.pipelineProv || 0) + (d.pipelineQuote || 0);
  const emailPct = d.contactCount > 0 ? Math.round((d.contactsWithEmail / d.contactCount) * 100) : 0;

  const actionIcon = (type) => {
    switch (type) {
      case 'Called': return '📞'; case 'Emailed': return '✉️';
      case 'Meeting Arranged': return '📅'; case 'Meeting Held': return '🤝';
      case 'Quote Sent': return '📄'; case 'Follow Up': return '🔔';
      case 'Status Changed': return '🔄'; case 'Reassigned': return '👤'; case 'Created': return '✨';
      default: return '📝';
    }
  };

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} />
      <div className="crm-content">
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]} 👋
        </h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          {(d.tasksOverdue > 0) && (
            <span onClick={() => navigate('/crm/tasks')} style={{ cursor: 'pointer', fontSize: 13, padding: '4px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600 }}>
              🔴 {d.tasksOverdue} overdue task{d.tasksOverdue !== 1 ? 's' : ''}
            </span>
          )}
          {(d.tasksDueToday > 0) && (
            <span onClick={() => navigate('/crm/tasks')} style={{ cursor: 'pointer', fontSize: 13, padding: '4px 12px', borderRadius: 8, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 600 }}>
              🟡 {d.tasksDueToday} task{d.tasksDueToday !== 1 ? 's' : ''} due today
            </span>
          )}
          {dueContacts.length > 0 && (
            <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 8, background: 'rgba(56,189,248,0.15)', color: '#38bdf8', fontWeight: 600 }}>
              📞 {dueContacts.length} contact{dueContacts.length !== 1 ? 's' : ''} to reach out to
            </span>
          )}
          {stalePipeline.length > 0 && (
            <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 8, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', fontWeight: 600 }}>
              ⚠️ {stalePipeline.length} stale opportunit{stalePipeline.length !== 1 ? 'ies' : 'y'}
            </span>
          )}
          {(d.tasksOverdue === 0 && d.tasksDueToday === 0 && dueContacts.length === 0 && stalePipeline.length === 0) && (
            <span style={{ fontSize: 13, padding: '4px 12px', borderRadius: 8, background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontWeight: 600 }}>
              ✅ You're all caught up!
            </span>
          )}
        </div>

        <div className="crm-kpi-row">
          <div className="crm-kpi-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/crm/clients')}>
            <div className="crm-kpi-label">MY CLIENTS</div>
            <div className="crm-kpi-value" style={{ color: C.purple }}>{d.clientCount || 0}</div>
            <div className="crm-kpi-sub">{d.contactCount || 0} contacts</div>
          </div>
          <div className="crm-kpi-card">
            <div className="crm-kpi-label">MY CLIENTS SPEND (YTD)</div>
            <div className="crm-kpi-value" style={{ color: C.confirmed }}>{fmtMoney(d.spendYTD)}</div>
            <div className="crm-kpi-sub">Lifetime: {fmtMoney(d.lifetimeValue)}</div>
          </div>
          <div className="crm-kpi-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/crm/opportunities')}>
            <div className="crm-kpi-label">OPEN PIPELINE</div>
            <div className="crm-kpi-value" style={{ color: C.green }}>{fmtMoney(p.openPipeline)}</div>
            <div className="crm-kpi-sub">Won: {fmtMoney(p.wonTotal)}</div>
          </div>
          <div className="crm-kpi-card">
            <div className="crm-kpi-label">EMAIL COVERAGE</div>
            <div className="crm-kpi-value" style={{ color: emailPct >= 80 ? C.green : emailPct >= 50 ? C.yellow : C.red }}>{emailPct}%</div>
            <div className="crm-kpi-sub">{d.contactsWithEmail || 0} of {d.contactCount || 0} contacts</div>
          </div>
        </div>

        {/* Pipeline visualization */}
        {(p.pipeline || []).length > 0 && (
          <div className="crm-dash-card" style={{ marginBottom: 16 }}>
            <h3 className="crm-dash-card-title">Opportunity Pipeline</h3>
            <div className="crm-pipeline-bar">
              {(p.pipeline || []).filter(s => !s.IsClosed && s.Count > 0).map(s => {
                const pct = p.openPipeline > 0 ? Math.max((s.TotalValue / p.openPipeline) * 100, 8) : 0;
                return (
                  <div key={s.Id} className="crm-pipeline-segment"
                    style={{ width: `${pct}%`, background: s.Color }}
                    title={`${s.Name}: ${s.Count} opps · ${fmtMoney(s.TotalValue)}`}
                    onClick={() => navigate('/crm/opportunities')}>
                    <span className="crm-pipeline-segment-label">{s.Name}</span>
                    <span className="crm-pipeline-segment-value">{fmtMoney(s.TotalValue)}</span>
                    <span className="crm-pipeline-segment-count">{s.Count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contact frequency reminders */}
        {dueContacts.length > 0 && (
          <div className="crm-dash-card" style={{ marginBottom: 16, borderLeft: '3px solid #fb923c' }}>
            <h3 className="crm-dash-card-title">📅 Contact Reminders</h3>
            <div className="crm-dash-list">
              {dueContacts.slice(0, 8).map(c => {
                const overdue = c.DaysUntilDue < 0;
                const quickLog = async (type, e) => {
                  e.stopPropagation();
                  await fetch('/api/crm/activities', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ entityType: c.EntityType, entityId: c.EntityId, activityType: type, subject: `Quick ${type.toLowerCase()} from dashboard` }),
                  });
                  setDueContacts(prev => prev.filter(x => !(x.EntityType === c.EntityType && x.EntityId === c.EntityId)));
                };
                return (
                  <div key={`${c.EntityType}-${c.EntityId}`} className="crm-dash-list-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(c.EntityType === 'company' ? `/crm/clients/${c.EntityId}` : `/crm/contacts/${c.EntityId}`)}>
                    <span style={{ fontSize: 14 }}>{c.EntityType === 'company' ? '🏢' : '👤'}</span>
                    <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>
                      {c.EntityName}
                      {c.ContactCompanyName && <span style={{ color: C.textDim, fontSize: 11, marginLeft: 6 }}>{c.ContactCompanyName}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button onClick={(e) => quickLog('Call', e)} title="Log call & mark done"
                        style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer', background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>📞</button>
                      <button onClick={(e) => quickLog('Email', e)} title="Log email & mark done"
                        style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer', background: 'rgba(56,189,248,0.15)', color: '#38bdf8' }}>✉️</button>
                      <button onClick={(e) => quickLog('Meeting', e)} title="Log meeting & mark done"
                        style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer', background: 'rgba(168,85,247,0.15)', color: '#a855f7' }}>🤝</button>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                        background: overdue ? '#ef444420' : '#fb923c20',
                        color: overdue ? '#ef4444' : '#fb923c',
                      }}>
                        {overdue ? `${Math.abs(c.DaysUntilDue)}d overdue` : c.DaysUntilDue === 0 ? 'Due today' : `${c.DaysUntilDue}d`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stale Pipeline Alerts */}
        {stalePipeline.length > 0 && (
          <div className="crm-dash-card" style={{ marginBottom: 16, borderLeft: '3px solid #ef4444' }}>
            <h3 className="crm-dash-card-title">⚠️ Stale Opportunities ({stalePipeline.length})</h3>
            <p style={{ fontSize: 12, color: C.textDim, marginBottom: 12 }}>Open opportunities with no activity for 7+ days</p>
            <div className="crm-dash-list">
              {stalePipeline.slice(0, 10).map(o => {
                const urgency = o.DaysSinceAction >= 30 ? '#ef4444' : o.DaysSinceAction >= 14 ? '#fb923c' : '#facc15';
                return (
                  <div key={o.Id} className="crm-dash-list-item" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/opportunities/${o.Id}`)}>
                    <span className="crm-status-badge" style={{ background: o.StatusColor, color: '#fff', fontSize: 10, padding: '2px 8px' }}>{o.StatusName}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{o.Title}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{o.CompanyName || 'No client'}{o.EstimatedValue ? ` · ${fmtMoney(o.EstimatedValue)}` : ''}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: urgency + '20', color: urgency, whiteSpace: 'nowrap' }}>
                      {o.DaysSinceAction}d idle
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sales Leaderboard */}
        {leaderboard && leaderboard.leaderboard.length > 0 && (
          <div className="crm-dash-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="crm-dash-card-title" style={{ margin: 0 }}>🏆 Sales Leaderboard — {leaderboard.year}</h3>
              <span style={{ fontSize: 11, color: C.textDim }}>YTD through {new Date(leaderboard.year, leaderboard.currentMonth - 1).toLocaleString('en-GB', { month: 'long' })}</span>
            </div>
            <div className="crm-table-wrap">
              <table className="crm-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th className="crm-th" style={{ width: 30, textAlign: 'center' }}>#</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>PERSON</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>CONFIRMED</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>YTD BUDGET</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>VARIANCE</th>
                    <th className="crm-th" style={{ textAlign: 'left', minWidth: 140 }}>PROGRESS</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>FULL YEAR</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.leaderboard.map((p, i) => {
                    const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`;
                    const barPct = Math.min(p.pctOfBudget, 150);
                    const barColor = p.pctOfBudget >= 100 ? '#4ade80' : p.pctOfBudget >= 75 ? '#fb923c' : '#ef4444';
                    return (
                      <tr key={p.name} className="crm-tr">
                        <td className="crm-td" style={{ textAlign: 'center', fontSize: i < 3 ? 16 : 13 }}>{medal}</td>
                        <td className="crm-td" style={{ fontWeight: 600 }}>{p.name}</td>
                        <td className="crm-td" style={{ textAlign: 'right', fontWeight: 700, color: C.confirmed }}>{fmtMoney(p.confirmed)}</td>
                        <td className="crm-td" style={{ textAlign: 'right', color: C.textMuted }}>{fmtMoney(p.ytdBudget)}</td>
                        <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600, color: p.variance >= 0 ? '#4ade80' : '#ef4444' }}>
                          {p.variance >= 0 ? '+' : ''}{fmtMoney(p.variance)}
                        </td>
                        <td className="crm-td">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, background: '#0f1117', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(barPct / 1.5, 100)}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: barColor, minWidth: 36, textAlign: 'right' }}>{p.pctOfBudget}%</span>
                          </div>
                        </td>
                        <td className="crm-td" style={{ textAlign: 'right', color: C.textDim, fontSize: 12 }}>{p.pctOfFullYear}% of {fmtMoney(p.budget)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Win/Loss Analytics */}
        {winLoss && winLoss.summary.total > 0 && (
          <div className="crm-winloss-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div className="crm-dash-card">
              <h3 className="crm-dash-card-title">📊 Win/Loss Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div onClick={() => navigate('/crm/opportunities?status=Won')} style={{ textAlign: 'center', padding: 12, background: 'rgba(74,222,128,0.1)', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#4ade80' }}>{winLoss.summary.won}</div>
                  <div style={{ fontSize: 11, color: '#4ade80' }}>Won · {fmtMoney(winLoss.summary.wonValue)}</div>
                </div>
                <div onClick={() => navigate('/crm/opportunities?status=Lost')} style={{ textAlign: 'center', padding: 12, background: 'rgba(239,68,68,0.1)', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{winLoss.summary.lost}</div>
                  <div style={{ fontSize: 11, color: '#ef4444' }}>Lost · {fmtMoney(winLoss.summary.lostValue)}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1, height: 12, background: '#ef444430', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ width: `${winLoss.summary.winRate}%`, height: '100%', background: '#4ade80', borderRadius: 6, transition: 'width 0.5s ease' }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: winLoss.summary.winRate >= 50 ? '#4ade80' : '#ef4444', minWidth: 45 }}>{winLoss.summary.winRate}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.textDim }}>
                <span>Avg deal: {fmtMoney(winLoss.summary.avgWonValue)}</span>
                <span>Avg close: {winLoss.summary.avgDaysToClose}d</span>
              </div>
            </div>
            {winLoss.byPerson.length > 0 && (
              <div className="crm-dash-card">
                <h3 className="crm-dash-card-title">Win Rate by Person</h3>
                <div className="crm-dash-list">
                  {winLoss.byPerson.map((p, i) => (
                    <div key={i} className="crm-dash-list-item" style={{ gap: 10 }}>
                      <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{p.Person || p.Email?.split('@')[0]}</div>
                      <div style={{ width: 100, height: 8, background: '#ef444430', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${p.winRate}%`, height: '100%', background: p.winRate >= 50 ? '#4ade80' : '#fb923c', borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: p.winRate >= 50 ? '#4ade80' : '#fb923c', minWidth: 35, textAlign: 'right' }}>{p.winRate}%</span>
                      <span style={{ fontSize: 11, color: C.textDim, minWidth: 65, textAlign: 'right' }}>{p.won}W/{p.lost}L</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="crm-dash-grid">
          {/* Top Clients */}
          <div className="crm-dash-card">
            <h3 className="crm-dash-card-title">Top Clients by YTD Spend</h3>
            {(d.topClients || []).length === 0 ? (
              <p style={{ color: C.textDim, fontSize: 13 }}>Assign clients to see your top spenders</p>
            ) : (
              <div className="crm-dash-list">
                {d.topClients.map((c, i) => (
                  <div key={c.CompanyCounter} className="crm-dash-list-item"
                    onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)} style={{ cursor: 'pointer' }}>
                    <div className="crm-dash-list-rank">{i + 1}</div>
                    <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{c.CompanyName}</div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.confirmed }}>{fmtMoney(c.SpendYTD)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="crm-dash-card">
            <h3 className="crm-dash-card-title">Recent Activity</h3>
            {(p.recentActivity || []).length === 0 ? (
              <p style={{ color: C.textDim, fontSize: 13 }}>No activity yet — log calls and meetings from Opportunities</p>
            ) : (
              <div className="crm-dash-list">
                {(p.recentActivity || []).slice(0, 8).map((a, i) => (
                  <div key={i} className="crm-dash-list-item" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/opportunities/${a.OppId}`)}>
                    <span style={{ fontSize: 14 }}>{actionIcon(a.ActionType)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.ActionType}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>
                        {a.OppTitle} · {a.CreatedByName || 'Unknown'}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: C.textDim }}>
                      {new Date(a.CreatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Jobs */}
          <div className="crm-dash-card">
            <h3 className="crm-dash-card-title">Recent Jobs</h3>
            {(d.recentJobs || []).length === 0 ? (
              <p style={{ color: C.textDim, fontSize: 13 }}>Assign clients to see their recent jobs</p>
            ) : (
              <div className="crm-dash-list">
                {d.recentJobs.map((j, i) => (
                  <div key={i} className="crm-dash-list-item">
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{j.JobTitle}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{j.Client} · {j.JobRef} · {j.DueOut}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtMoney(j.ConfirmedIncome)}</div>
                      <span className={`crm-status-badge crm-status-${(j.Status || '').toLowerCase().replace(/\s/g, '-')}`} style={{ fontSize: 10 }}>
                        {j.Status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Team Activity (managers+) */}
          {canAccess(user, 'manager') && (p.activityByPerson || []).length > 0 && (
            <div className="crm-dash-card">
              <h3 className="crm-dash-card-title">Team Activity (30 days)</h3>
              <div className="crm-dash-list">
                {(p.activityByPerson || []).map((a, i) => (
                  <div key={i} className="crm-dash-list-item">
                    <div className="crm-dash-list-rank">{i + 1}</div>
                    <div style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{a.DisplayName || a.Email.split('@')[0]}</div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.purple }}>{a.ActionCount} actions</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// PLACEHOLDER PAGES
// ══════════════════════════════════════════════

// ══════════════════════════════════════════════
// CREATE NEW CLIENT / CONTACT
// ══════════════════════════════════════════════

const CrmNewClient = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('client'); // client or contact
  // Client fields
  const [companyName, setCompanyName] = useState('');
  const [town, setTown] = useState('');
  const [postCode, setPostCode] = useState('');
  const [telephone, setTelephone] = useState('');
  const [emailAddr, setEmailAddr] = useState('');
  
  // Contact fields
  const [forename, setForename] = useState('');
  const [surname, setSurname] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [linkCompany, setLinkCompany] = useState('');
  const [linkCompanyId, setLinkCompanyId] = useState(null);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  // State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const searchCompany = (q) => {
    setLinkCompany(q);
    setLinkCompanyId(null);
    if (q.length < 2) { setCompanySuggestions([]); return; }
    clearTimeout(window._newCompSearchTimer);
    window._newCompSearchTimer = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(q)}`).then(r => r.ok ? r.json() : {})
        .then(d => setCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
  };

  const createClient = async () => {
    if (!companyName.trim()) return;
    setSaving(true); setError('');
    const res = await fetch('/api/crm/create-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName: companyName.trim(), town, postCode, telephone, email: emailAddr, address1: '' }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) {
      if (res.status === 409 && data.existingId) {
        setError(`Company "${companyName}" already exists in HireTrack`);
        setTimeout(() => navigate(`/crm/clients/${data.existingId}`), 1500);
      } else { setError(data.error || 'Failed to create client'); }
      return;
    }
    setSuccess({ type: 'client', name: companyName.trim(), id: data.companyCounter });
    setCompanyName(''); setTown(''); setPostCode(''); setTelephone(''); setEmailAddr('');
  };

  const createContact = async () => {
    if (!surname.trim()) return;
    setSaving(true); setError('');
    const res = await fetch('/api/crm/create-contact', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forename, surname: surname.trim(), email: contactEmail, mobile, telephone: contactPhone, companyCounter: linkCompanyId, title: '' }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setError(data.error || 'Failed to create contact'); return; }
    setSuccess({ type: 'contact', name: `${forename} ${surname}`.trim(), id: data.nameCounter });
    setForename(''); setSurname(''); setContactEmail(''); setMobile(''); setContactPhone(''); setLinkCompany(''); setLinkCompanyId(null);
  };

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} />
      <div className="crm-content">
        <h2 className="crm-page-title">Create New</h2>
        <p className="crm-page-subtitle">Add a new client or contact — creates in both HireTrack and the CRM</p>

        <div className="crm-view-toggle" style={{ marginBottom: 16, marginTop: 12 }}>
          <button className={`crm-toggle-btn ${mode === 'client' ? 'active' : ''}`} onClick={() => { setMode('client'); setError(''); setSuccess(null); }}>
            🏢 New Client
          </button>
          <button className={`crm-toggle-btn ${mode === 'contact' ? 'active' : ''}`} onClick={() => { setMode('contact'); setError(''); setSuccess(null); }}>
            👤 New Contact
          </button>
        </div>

        {success && (
          <div className="crm-success-banner">
            ✅ {success.type === 'client' ? 'Client' : 'Contact'} "{success.name}" created in HireTrack and the CRM.
            {success.id && (
              <button onClick={() => navigate(`/crm/${success.type === 'client' ? 'clients' : 'contacts'}/${success.id}`)}
                style={{ marginLeft: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(74,222,128,0.4)', background: 'rgba(74,222,128,0.15)', color: '#4ade80', cursor: 'pointer', fontSize: 12 }}>
                View →
              </button>
            )}
          </div>
        )}

        {error && <div className="crm-error-banner">⚠ {error}</div>}

        {mode === 'client' && (
          <div className="crm-create-card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New Client Company</h3>
            <div className="crm-create-grid">
              <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                <label>Company Name *</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Productions Ltd" className="crm-input" autoFocus />
              </div>
              <div className="crm-create-field">
                <label>Telephone</label>
                <input type="text" value={telephone} onChange={e => setTelephone(e.target.value)}
                  placeholder="e.g. 020 1234 5678" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Email</label>
                <input type="email" value={emailAddr} onChange={e => setEmailAddr(e.target.value)}
                  placeholder="e.g. info@acme.com" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Town</label>
                <input type="text" value={town} onChange={e => setTown(e.target.value)}
                  placeholder="e.g. London" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Post Code</label>
                <input type="text" value={postCode} onChange={e => setPostCode(e.target.value)}
                  placeholder="e.g. SW1A 1AA" className="crm-input" />
              </div>
              <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                <button className="crm-btn-primary" onClick={createClient}
                  disabled={!companyName.trim() || saving} style={{ opacity: companyName.trim() && !saving ? 1 : 0.4 }}>
                  {saving ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </div>
          </div>
        )}

        {mode === 'contact' && (
          <div className="crm-create-card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New Contact</h3>
            <div className="crm-create-grid">
              <div className="crm-create-field">
                <label>First Name</label>
                <input type="text" value={forename} onChange={e => setForename(e.target.value)}
                  placeholder="e.g. John" className="crm-input" autoFocus />
              </div>
              <div className="crm-create-field">
                <label>Surname *</label>
                <input type="text" value={surname} onChange={e => setSurname(e.target.value)}
                  placeholder="e.g. Smith" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Email</label>
                <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                  placeholder="e.g. john@acme.com" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Mobile</label>
                <input type="text" value={mobile} onChange={e => setMobile(e.target.value)}
                  placeholder="e.g. 07700 900000" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Telephone</label>
                <input type="text" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
                  placeholder="e.g. 020 1234 5678" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Link to Company</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={linkCompany} onChange={e => searchCompany(e.target.value)}
                    placeholder="Search company..." className="crm-input" />
                  {linkCompanyId && <span style={{ position: 'absolute', right: 10, top: 8, color: C.green, fontSize: 14 }}>✓</span>}
                  {companySuggestions.length > 0 && (
                    <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                      {companySuggestions.map(c => (
                        <div key={c.CompanyCounter} className="crm-search-item" onClick={() => {
                          setLinkCompanyId(c.CompanyCounter);
                          setLinkCompany(c.CompanyName);
                          setCompanySuggestions([]);
                        }}>
                          <span className="crm-search-item-title">{c.CompanyName}</span>
                          <span className="crm-search-item-sub">{c.Town || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                <button className="crm-btn-primary" onClick={createContact}
                  disabled={!surname.trim() || saving} style={{ opacity: surname.trim() && !saving ? 1 : 0.4 }}>
                  {saving ? 'Creating...' : 'Create Contact'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// UNASSIGNED CLIENT REVIEW (Manager+)
// ══════════════════════════════════════════════

const CrmReviewClients = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [salesUsers, setSalesUsers] = useState([]);
  const [assigning, setAssigning] = useState({});

  useEffect(() => {
    fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []).then(setSalesUsers);
  }, []);

  const fetchClients = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    if (search) params.set('q', search);
    fetch(`/api/crm/unassigned-clients?${params}`)
      .then(r => r.ok ? r.json() : { clients: [], total: 0 })
      .then(d => { setClients(d.clients || []); setTotal(d.total || 0); setLoading(false); });
  }, [page, search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSearch = (q) => {
    setSearch(q);
    setPage(1);
  };

  const assignClient = async (companyCounter, email) => {
    if (!email) return;
    setAssigning(prev => ({ ...prev, [companyCounter]: true }));
    await fetch('/api/crm/assign-client', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyCounter, assignTo: email }),
    });
    // Remove from list
    setClients(prev => prev.filter(c => c.CompanyCounter !== companyCounter));
    setTotal(prev => prev - 1);
    setAssigning(prev => ({ ...prev, [companyCounter]: false }));
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <input type="text" placeholder="Search unassigned clients..." value={search}
          onChange={e => handleSearch(e.target.value)} className="crm-search-input" />
      </CrmTopBar>
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">Review Unassigned Clients</h2>
            <p className="crm-page-subtitle">{total.toLocaleString()} clients without an account manager</p>
          </div>
        </div>

        {loading ? <Loading /> : (
          <div className="crm-table-card">
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th className="crm-th" style={{ textAlign: 'left' }}>COMPANY</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>TOWN</th>
                    <th className="crm-th" style={{ textAlign: 'center' }}>CONTACTS</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>SPEND (YTD)</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>LIFETIME</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>CREATED BY</th>
                    <th className="crm-th" style={{ textAlign: 'center', minWidth: 180 }}>ASSIGN TO</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: C.textDim, padding: 40 }}>
                      {search ? 'No matching unassigned clients' : 'All clients are assigned!'}
                    </td></tr>
                  )}
                  {clients.map(c => (
                    <tr key={c.CompanyCounter} className="crm-tr">
                      <td className="crm-td">
                        <span style={{ fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                          {c.CompanyName}
                        </span>
                      </td>
                      <td className="crm-td">{c.Town || '–'}</td>
                      <td className="crm-td" style={{ textAlign: 'center' }}>{c.ContactCount}</td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: c.SpendYTD > 0 ? 600 : 400 }}>
                        {fmtMoney(c.SpendYTD)}
                      </td>
                      <td className="crm-td" style={{ textAlign: 'right' }}>{fmtMoney(c.LifetimeValue)}</td>
                      <td className="crm-td">
                        {c.CrmCreatedByName ? (
                          <span className="crm-assignee-tag" style={{ background: '#7c3aed30', color: '#a78bfa' }}>
                            {c.CrmCreatedByName.split(' ')[0]} (CRM)
                          </span>
                        ) : (
                          <span style={{ color: C.textDim, fontSize: 12 }}>HireTrack</span>
                        )}
                      </td>
                      <td className="crm-td" style={{ textAlign: 'center' }}>
                        {assigning[c.CompanyCounter] ? (
                          <span style={{ color: C.textDim, fontSize: 12 }}>Assigning...</span>
                        ) : (
                          <select className="crm-filter-select" defaultValue=""
                            onChange={e => assignClient(c.CompanyCounter, e.target.value)}>
                            <option value="" disabled>Select person...</option>
                            {c.CrmCreatedBy && (
                              <option value={c.CrmCreatedBy}>⭐ {c.CrmCreatedByName || c.CrmCreatedBy} (creator)</option>
                            )}
                            {salesUsers.filter(u => u.Email !== c.CrmCreatedBy).map(u => (
                              <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
                <button className="admin-sub-action" disabled={page <= 1}
                  style={{ opacity: page <= 1 ? 0.3 : 1 }}
                  onClick={() => setPage(p => p - 1)}>← Previous</button>
                <span style={{ fontSize: 12, color: C.textMuted, padding: '5px 8px' }}>
                  Page {page} of {totalPages}
                </span>
                <button className="admin-sub-action" disabled={page >= totalPages}
                  style={{ opacity: page >= totalPages ? 0.3 : 1 }}
                  onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// OPPORTUNITIES & LEADS
// ══════════════════════════════════════════════

const ACTION_TYPES = ['Called', 'Emailed', 'Meeting Arranged', 'Meeting Held', 'Quote Sent', 'Follow Up', 'Note', 'Status Changed'];

const CrmOpportunities = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [opps, setOpps] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const urlParams = new URLSearchParams(location.search);
  const urlStatus = urlParams.get('status') || '';
  const savedView = (() => { try { return sessionStorage.getItem('crm-opp-view') || 'list'; } catch { return 'list'; } })();
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClosed, setFilterClosed] = useState(urlStatus ? '1' : savedView === 'kanban' ? '' : '0');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [viewMode, setViewMode] = useState(savedView);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [urlStatusApplied, setUrlStatusApplied] = useState(false);

  // Create form
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newStatusId, setNewStatusId] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newCloseDate, setNewCloseDate] = useState('');
  const [newAssignedTo, setNewAssignedTo] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [newIsRecurring, setNewIsRecurring] = useState(false);
  const [newRecurringInterval, setNewRecurringInterval] = useState('30');
  const [newIsTender, setNewIsTender] = useState(false);
  const [newTenderEndDate, setNewTenderEndDate] = useState('');
  const fetchOpps = useCallback(() => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('statusId', filterStatus);
    if (filterClosed) params.set('closed', filterClosed);
    if (filterAssigned) params.set('assignedTo', filterAssigned);
    fetch(`/api/crm/opportunities?${params}`).then(r => r.ok ? r.json() : []).then(setOpps);
  }, [filterStatus, filterClosed, filterAssigned]);

  useEffect(() => {
    Promise.all([
      fetch('/api/crm/opportunity-statuses').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []),
      fetch(`/api/crm/pipeline-summary${filterAssigned ? `?assignedTo=${filterAssigned}` : ''}`).then(r => r.ok ? r.json() : null),
    ]).then(([st, users, pipeline]) => {
      setStatuses(st);
      setSalesUsers(users);
      setPipelineSummary(pipeline);
      if (st.length) setNewStatusId(String(st[0].Id));
      if (urlStatus && st.length && !urlStatusApplied) {
        const match = st.find(s => s.Name.toLowerCase() === urlStatus.toLowerCase());
        if (match) setFilterStatus(String(match.Id));
        setUrlStatusApplied(true);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => { if (!loading) fetchOpps(); }, [fetchOpps, loading]);

  const searchCompany = (q) => {
    setNewCompany(q);
    setSelectedCompany(null);
    if (q.length < 2) { setCompanySuggestions([]); return; }
    clearTimeout(window._compSearchTimer);
    window._compSearchTimer = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(q)}`).then(r => r.ok ? r.json() : {})
        .then(d => setCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
  };

  const selectCompany = (c) => {
    setSelectedCompany(c);
    setNewCompany(c.CompanyName);
    setCompanySuggestions([]);
    setSelectedContactId('');
    // Fetch contacts for this company
    fetch(`/api/crm/client/${c.CompanyCounter}`).then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.contacts) setCompanyContacts(d.contacts); });
  };

  const createOpp = async () => {
    if (!newTitle.trim() || !newStatusId) return;
    const body = {
      title: newTitle.trim(),
      description: newDesc.trim() || null,
      statusId: parseInt(newStatusId),
      companyCounter: selectedCompany?.CompanyCounter || null,
      contactId: selectedContactId ? parseInt(selectedContactId) : null,
      estimatedValue: newValue ? parseFloat(newValue) : null,
      expectedCloseDate: newCloseDate || null,
      assignedTo: newAssignedTo || user.email,
      isRecurring: newIsRecurring,
      recurringIntervalDays: newIsRecurring ? parseInt(newRecurringInterval) : null,
      isTender: newIsTender,
      tenderEndDate: newIsTender ? newTenderEndDate : null,
    };
    const res = await fetch('/api/crm/opportunity', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      setShowCreate(false);
      setNewTitle(''); setNewDesc(''); setNewValue(''); setNewCloseDate(''); setNewCompany(''); setSelectedCompany(null);
      setNewIsRecurring(false); setNewRecurringInterval('30');
      navigate(`/crm/opportunities/${data.id}`);
    }
  };

  // Refresh pipeline when assigned filter changes
  useEffect(() => {
    fetch(`/api/crm/pipeline-summary${filterAssigned ? `?assignedTo=${filterAssigned}` : ''}`)
      .then(r => r.ok ? r.json() : null).then(setPipelineSummary);
  }, [filterAssigned]);

  // Drag & drop for kanban
  const handleDragStart = (e, oppId) => {
    setDraggingId(oppId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(oppId));
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(statusId);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e, newStatusId) => {
    e.preventDefault();
    setDragOverCol(null);
    const oppId = draggingId;
    setDraggingId(null);
    if (!oppId) return;
    const opp = opps.find(o => o.Id === oppId);
    if (!opp || opp.StatusId === newStatusId) return;

    // Optimistic update
    setOpps(prev => prev.map(o => o.Id === oppId ? { ...o, StatusId: newStatusId, StatusName: statuses.find(s => s.Id === newStatusId)?.Name, StatusColor: statuses.find(s => s.Id === newStatusId)?.Color } : o));

    await fetch(`/api/crm/opportunity/${oppId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statusId: newStatusId }),
    });
    const st = statuses.find(s => s.Id === newStatusId);
    await fetch(`/api/crm/opportunity/${oppId}/action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionType: 'Status Changed', note: `Moved to "${st?.Name}"` }),
    });
    fetchOpps();
  };

  const filtered = opps.filter(o => {
    if (search) {
      const s = search.toLowerCase();
      if (!(o.Title || '').toLowerCase().includes(s)
        && !(o.CompanyName || '').toLowerCase().includes(s)
        && !(o.AssignedName || '').toLowerCase().includes(s)
        && !(o.JobRef || '').toLowerCase().includes(s)) return false;
    }
    if (dateFrom && o.ExpectedCloseDate && o.ExpectedCloseDate < dateFrom) return false;
    if (dateTo && o.ExpectedCloseDate && o.ExpectedCloseDate > dateTo + 'T23:59:59') return false;
    return true;
  });

  const totalValue = filtered.reduce((sum, o) => sum + (o.EstimatedValue || 0), 0);

  if (loading) return <Loading />;

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle}>
        <input type="text" placeholder="Search opportunities..." value={search}
          onChange={e => setSearch(e.target.value)} className="crm-search-input" />
      </CrmTopBar>
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">Opportunities & Leads</h2>
            <p className="crm-page-subtitle">{filtered.length} opportunities · Pipeline: {fmtMoney(totalValue)}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canAccess(user, 'manager') && <button className="crm-btn-primary" onClick={() => {
              exportCSV(`opportunities-${new Date().toISOString().split('T')[0]}.csv`,
                ['Title', 'Company', 'Status', 'Value', 'Expected Close', 'Assigned To', 'Recurring', 'Tender', 'Tender End', 'Created'],
                filtered.map(o => [o.Title, o.CompanyName, o.StatusName, o.EstimatedValue || 0, o.ExpectedCloseDate ? new Date(o.ExpectedCloseDate).toLocaleDateString('en-GB') : '', o.AssignedName || o.AssignedTo, o.IsRecurring ? 'Yes' : 'No', o.IsTender ? 'Yes' : 'No', o.TenderEndDate ? new Date(o.TenderEndDate).toLocaleDateString('en-GB') : '', new Date(o.CreatedAt).toLocaleDateString('en-GB')])
              );
            }} style={{ fontSize: 12, padding: '6px 14px', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}>
              ↓ Export CSV
            </button>}
            <div className="crm-view-toggle">
              <button className={`crm-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => { setViewMode('list'); try { sessionStorage.setItem('crm-opp-view', 'list'); } catch {} }}>☰ List</button>
              <button className={`crm-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`} onClick={() => { setViewMode('kanban'); setFilterClosed(''); try { sessionStorage.setItem('crm-opp-view', 'kanban'); } catch {} }}>▦ Board</button>
            </div>
            <button className="crm-btn-primary" onClick={() => setShowCreate(!showCreate)}>
              {showCreate ? '✕ Cancel' : '+ New Opportunity'}
            </button>
          </div>
        </div>

        {/* Pipeline summary for managers */}
        {canAccess(user, 'manager') && pipelineSummary && (pipelineSummary.pipeline || []).some(s => s.Count > 0) && (
          <div className="crm-pipeline-summary">
            <div className="crm-pipeline-bar">
              {(pipelineSummary.pipeline || []).filter(s => !s.IsClosed && s.Count > 0).map(s => {
                const pct = pipelineSummary.openPipeline > 0 ? Math.max((s.TotalValue / pipelineSummary.openPipeline) * 100, 8) : 0;
                return (
                  <div key={s.Id} className="crm-pipeline-segment"
                    style={{ width: `${pct}%`, background: s.Color, cursor: 'pointer' }}
                    onClick={() => { setFilterStatus(String(s.Id)); setFilterClosed(''); }}>
                    <span className="crm-pipeline-segment-label">{s.Name}</span>
                    <span className="crm-pipeline-segment-value">{fmtMoney(s.TotalValue)}</span>
                    <span className="crm-pipeline-segment-count">{s.Count} opps</span>
                  </div>
                );
              })}
            </div>
            <div className="crm-pipeline-stats">
              <div className="crm-pipeline-stat">
                <span className="crm-pipeline-stat-label">Open Pipeline</span>
                <span className="crm-pipeline-stat-value" style={{ color: C.green }}>{fmtMoney(pipelineSummary.openPipeline)}</span>
              </div>
              <div className="crm-pipeline-stat">
                <span className="crm-pipeline-stat-label">Won</span>
                <span className="crm-pipeline-stat-value" style={{ color: C.confirmed }}>{fmtMoney(pipelineSummary.wonTotal)}</span>
              </div>
              {(pipelineSummary.activityByPerson || []).length > 0 && (
                <div className="crm-pipeline-stat">
                  <span className="crm-pipeline-stat-label">Most Active (30d)</span>
                  <span className="crm-pipeline-stat-value" style={{ color: C.purple }}>{pipelineSummary.activityByPerson[0]?.DisplayName?.split(' ')[0] || '–'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="crm-opp-filters">
          <div className="crm-view-toggle" style={{ marginRight: 8 }}>
            <button className={`crm-toggle-btn ${filterClosed === '0' ? 'active' : ''}`} onClick={() => setFilterClosed('0')}>Open</button>
            <button className={`crm-toggle-btn ${filterClosed === '1' ? 'active' : ''}`} onClick={() => setFilterClosed('1')}>Closed</button>
            <button className={`crm-toggle-btn ${filterClosed === '' ? 'active' : ''}`} onClick={() => setFilterClosed('')}>All</button>
          </div>
          {viewMode !== 'kanban' && (
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="crm-filter-select">
              <option value="">All Statuses</option>
              {statuses.map(s => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
            </select>
          )}
          {canAccess(user, 'manager') && (
            <select value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)} className="crm-filter-select">
              <option value="">All People</option>
              {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
            </select>
          )}
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="crm-filter-select" title="Close date from" style={{ width: 'auto' }} />
          <span style={{ color: C.textDim, fontSize: 11 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="crm-filter-select" title="Close date to" style={{ width: 'auto' }} />
          {(dateFrom || dateTo) && (
            <button className="crm-sub-filter-clear" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>
          )}
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="crm-create-card">
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>New Opportunity</h3>
            <div className="crm-create-grid">
              <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                <label>Title *</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  placeholder="e.g. Annual conference lighting package" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Client</label>
                <div style={{ position: 'relative' }}>
                  <input type="text" value={newCompany} onChange={e => searchCompany(e.target.value)}
                    placeholder="Search company..." className="crm-input" />
                  {companySuggestions.length > 0 && (
                    <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                      {companySuggestions.map(c => (
                        <div key={c.CompanyCounter} className="crm-search-item" onClick={() => selectCompany(c)}>
                          <span className="crm-search-item-title">{c.CompanyName}</span>
                          <span className="crm-search-item-sub">{c.Town || ''}</span>
                        </div>
                      ))}
                      <div className="crm-search-item" style={{ borderTop: '1px solid rgba(148,163,184,0.2)', color: '#4ade80', fontWeight: 600 }}
                        onClick={async () => {
                          const name = newCompany.trim();
                          if (!name) return;
                          const res = await fetch('/api/crm/create-client', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ companyName: name }),
                          });
                          const data = await res.json();
                          if (res.ok && data.companyCounter) {
                            selectCompany({ CompanyCounter: data.companyCounter, CompanyName: name });
                          } else if (data.existingId) {
                            selectCompany({ CompanyCounter: data.existingId, CompanyName: name });
                          } else {
                            alert(data.error || 'Failed to create client');
                          }
                        }}>
                        <span className="crm-search-item-title">+ Create "{newCompany.trim()}"</span>
                        <span className="crm-search-item-sub">Add as new client</span>
                      </div>
                    </div>
                  )}
                  {newCompany.length >= 2 && companySuggestions.length === 0 && !selectedCompany && (
                    <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                      <div className="crm-search-item" style={{ color: '#4ade80', fontWeight: 600 }}
                        onClick={async () => {
                          const name = newCompany.trim();
                          if (!name) return;
                          const res = await fetch('/api/crm/create-client', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ companyName: name }),
                          });
                          const data = await res.json();
                          if (res.ok && data.companyCounter) {
                            selectCompany({ CompanyCounter: data.companyCounter, CompanyName: name });
                          } else if (data.existingId) {
                            selectCompany({ CompanyCounter: data.existingId, CompanyName: name });
                          } else {
                            alert(data.error || 'Failed to create client');
                          }
                        }}>
                        <span className="crm-search-item-title">+ Create "{newCompany.trim()}"</span>
                        <span className="crm-search-item-sub">No matches — add as new client</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="crm-create-field">
                <label>Status</label>
                <select value={newStatusId} onChange={e => setNewStatusId(e.target.value)} className="crm-input">
                  {statuses.filter(s => !s.IsClosed).map(s => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                </select>
              </div>
              {selectedCompany && companyContacts.length > 0 && (
                <div className="crm-create-field">
                  <label>Contact</label>
                  <select value={selectedContactId} onChange={e => setSelectedContactId(e.target.value)} className="crm-input">
                    <option value="">— No specific contact —</option>
                    {companyContacts.map(c => <option key={c.NameCounter} value={c.NameCounter}>{c.FORENAME} {c.SURNAME}{c.EMAIL ? ` (${c.EMAIL})` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="crm-create-field">
                <label>Estimated Value (£)</label>
                <input type="number" value={newValue} onChange={e => setNewValue(e.target.value)}
                  placeholder="0" className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label>Expected Close Date</label>
                <input type="date" value={newCloseDate} onChange={e => setNewCloseDate(e.target.value)} className="crm-input" />
              </div>
              <div className="crm-create-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={newIsRecurring} onChange={e => setNewIsRecurring(e.target.checked)} />
                  Recurring Project
                </label>
                {newIsRecurring && (
                  <select value={newRecurringInterval} onChange={e => setNewRecurringInterval(e.target.value)} className="crm-input" style={{ marginTop: 6 }}>
                    <option value="7">Weekly</option>
                    <option value="14">Fortnightly</option>
                    <option value="30">Monthly</option>
                    <option value="60">Every 2 Months</option>
                    <option value="90">Quarterly</option>
                    <option value="182">Every 6 Months</option>
                    <option value="365">Yearly</option>
                  </select>
                )}
              </div>
              <div className="crm-create-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={newIsTender} onChange={e => setNewIsTender(e.target.checked)} />
                  Tender
                </label>
                {newIsTender && (
                  <div style={{ marginTop: 6 }}>
                    <label style={{ fontSize: 11, color: C.textDim }}>Tender End Date</label>
                    <input type="date" value={newTenderEndDate} onChange={e => setNewTenderEndDate(e.target.value)} className="crm-input" />
                  </div>
                )}
              </div>
              {canAccess(user, 'manager') && (
                <div className="crm-create-field">
                  <label>Assign To</label>
                  <select value={newAssignedTo} onChange={e => setNewAssignedTo(e.target.value)} className="crm-input">
                    <option value="">Myself</option>
                    {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
                  </select>
                </div>
              )}
              <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  placeholder="Optional notes..." className="crm-input" rows={3} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <button className="crm-btn-primary" onClick={createOpp}
                  disabled={!newTitle.trim()} style={{ opacity: newTitle.trim() ? 1 : 0.4 }}>
                  Create Opportunity
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Opportunity list */}
        {viewMode === 'list' && (
        <div className="crm-table-card">
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th className="crm-th" style={{ textAlign: 'left' }}>OPPORTUNITY</th>
                  <th className="crm-th" style={{ textAlign: 'left' }}>CLIENT</th>
                  <th className="crm-th" style={{ textAlign: 'center' }}>STATUS</th>
                  <th className="crm-th" style={{ textAlign: 'right' }}>VALUE</th>
                  <th className="crm-th" style={{ textAlign: 'left' }}>ASSIGNED TO</th>
                  <th className="crm-th" style={{ textAlign: 'left' }}>CLOSE DATE</th>
                  <th className="crm-th" style={{ textAlign: 'left' }}>UPDATED</th>
                  <th style={{ width: 30 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: C.textDim, padding: 40 }}>
                    No opportunities found
                  </td></tr>
                )}
                {filtered.map(o => (
                  <tr key={o.Id} className="crm-tr" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/opportunities/${o.Id}`)}>
                    <td className="crm-td">
                      <div style={{ fontWeight: 600 }}>{o.Title}
                        {o.IsRecurring ? <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(124,58,237,0.2)', color: '#a78bfa', marginLeft: 6, fontWeight: 600 }}>🔄 Recurring</span> : null}
                        {o.IsTender ? <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(251,146,60,0.2)', color: '#fb923c', marginLeft: 6, fontWeight: 600 }}>📋 Tender{o.TenderEndDate ? ` · ${new Date(o.TenderEndDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}` : ''}</span> : null}
                      </div>
                      {o.JobRef && <div className="crm-td-sub">Job: {o.JobRef}</div>}
                    </td>
                    <td className="crm-td">{o.CompanyName || '–'}</td>
                    <td className="crm-td" style={{ textAlign: 'center' }}>
                      <span className="crm-status-badge" style={{ background: o.StatusColor, color: '#fff' }}>
                        {o.StatusName}
                      </span>
                    </td>
                    <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(o.EstimatedValue)}</td>
                    <td className="crm-td">
                      <span className="crm-assignee-tag">{(o.AssignedName || o.AssignedTo || '').split(' ')[0]}</span>
                    </td>
                    <td className="crm-td" style={{ fontSize: 12, color: C.textMuted }}>
                      {o.ExpectedCloseDate ? new Date(o.ExpectedCloseDate).toLocaleDateString('en-GB') : '–'}
                    </td>
                    <td className="crm-td" style={{ fontSize: 12, color: C.textDim }}>
                      {new Date(o.UpdatedAt).toLocaleDateString('en-GB')}
                    </td>
                    <td className="crm-td" style={{ color: C.textDim }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Kanban board */}
        {viewMode === 'kanban' && (
          <div className="crm-kanban">
            {statuses.filter(s => s.IsActive).map(s => {
              const colOpps = filtered.filter(o => o.StatusId === s.Id);
              const colTotal = colOpps.reduce((sum, o) => sum + (o.EstimatedValue || 0), 0);
              const isOver = dragOverCol === s.Id;
              return (
                <div key={s.Id} className={`crm-kanban-col ${isOver ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, s.Id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, s.Id)}>
                  <div className="crm-kanban-header" style={{ borderTopColor: s.Color }}>
                    <span style={{ fontWeight: 700, fontSize: 12 }}>{s.Name}</span>
                    <span style={{ fontSize: 11, color: C.textDim }}>{colOpps.length} · {fmtMoney(colTotal)}</span>
                  </div>
                  <div className="crm-kanban-cards">
                    {colOpps.map(o => (
                      <div key={o.Id} className={`crm-kanban-card ${draggingId === o.Id ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, o.Id)}
                        onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                        onClick={() => navigate(`/crm/opportunities/${o.Id}`)}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{o.Title}</div>
                        {o.CompanyName && <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>🏢 {o.CompanyName}</div>}
                        {(o.IsRecurring || o.IsTender) && (
                          <div style={{ display: 'flex', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
                            {o.IsRecurring && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(124,58,237,0.2)', color: '#a78bfa', fontWeight: 600 }}>🔄 Recurring</span>}
                            {o.IsTender && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(251,146,60,0.2)', color: '#fb923c', fontWeight: 600 }}>📋 Tender{o.TenderEndDate ? ` · ${new Date(o.TenderEndDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}` : ''}</span>}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: C.green }}>{fmtMoney(o.EstimatedValue)}</span>
                          <span className="crm-assignee-tag" style={{ fontSize: 10 }}>
                            {(o.AssignedName || '').split(' ')[0]}
                          </span>
                        </div>
                        {o.ExpectedCloseDate && (
                          <div style={{ fontSize: 10, color: C.textDim, marginTop: 4 }}>
                            📅 {new Date(o.ExpectedCloseDate).toLocaleDateString('en-GB')}
                          </div>
                        )}
                      </div>
                    ))}
                    {colOpps.length === 0 && (
                      <div className="crm-kanban-empty">
                        Drop here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

// ── OPPORTUNITY DETAIL ──
const CrmOpportunityDetail = ({ user, onMenuToggle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  // Action form
  const [actionType, setActionType] = useState('Called');
  const [actionNote, setActionNote] = useState('');
  // Company search for edit
  const [editCompany, setEditCompany] = useState('');
  const [editCompanySuggestions, setEditCompanySuggestions] = useState([]);

  const fetchOpp = useCallback(() => {
    fetch(`/api/crm/opportunity/${id}`).then(r => r.ok ? r.json() : null).then(data => {
      setOpp(data);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    Promise.all([
      fetch('/api/crm/opportunity-statuses').then(r => r.ok ? r.json() : []),
      fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []),
    ]).then(([st, users]) => { setStatuses(st); setSalesUsers(users); });
    fetchOpp();
  }, [fetchOpp]);

  const changeStatus = async (statusId) => {
    await fetch(`/api/crm/opportunity/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statusId }),
    });
    // Auto-log status change
    const st = statuses.find(s => s.Id === statusId);
    await fetch(`/api/crm/opportunity/${id}/action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionType: 'Status Changed', note: `Changed to "${st?.Name}"` }),
    });
    fetchOpp();
  };

  const addAction = async () => {
    if (!actionType) return;
    await fetch(`/api/crm/opportunity/${id}/action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actionType, note: actionNote || null }),
    });
    setActionNote('');
    fetchOpp();
  };

  const startEdit = () => {
    setEditing(true);
    setEditData({
      title: opp.Title, description: opp.Description || '',
      estimatedValue: opp.EstimatedValue || '', expectedCloseDate: opp.ExpectedCloseDate ? opp.ExpectedCloseDate.split('T')[0] : '',
      jobRef: opp.JobRef || '', assignedTo: opp.AssignedTo,
      companyCounter: opp.CompanyCounter,
      isRecurring: opp.IsRecurring || false,
      recurringIntervalDays: opp.RecurringIntervalDays || '30',
      competitor: opp.Competitor || '',
      lostReason: opp.LostReason || '',
      isTender: opp.IsTender || false,
      tenderEndDate: opp.TenderEndDate ? opp.TenderEndDate.split('T')[0] : '',
    });
    setEditCompany(opp.CompanyName || '');
  };

  const saveEdit = async () => {
    await fetch(`/api/crm/opportunity/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: editData.title, description: editData.description || null,
        estimatedValue: editData.estimatedValue ? parseFloat(editData.estimatedValue) : null,
        expectedCloseDate: editData.expectedCloseDate || null,
        jobRef: editData.jobRef || null, assignedTo: editData.assignedTo,
        companyCounter: editData.companyCounter || null,
        isRecurring: editData.isRecurring,
        recurringIntervalDays: editData.isRecurring ? parseInt(editData.recurringIntervalDays) : null,
        competitor: editData.competitor || null,
        lostReason: editData.lostReason || null,
        isTender: editData.isTender,
        tenderEndDate: editData.isTender ? editData.tenderEndDate || null : null,
      }),
    });
    setEditing(false);
    fetchOpp();
  };

  const searchEditCompany = (q) => {
    setEditCompany(q);
    setEditData(prev => ({ ...prev, companyCounter: null }));
    if (q.length < 2) { setEditCompanySuggestions([]); return; }
    clearTimeout(window._editCompSearchTimer);
    window._editCompSearchTimer = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(q)}`).then(r => r.ok ? r.json() : {})
        .then(d => setEditCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
  };

  if (loading) return <Loading />;
  if (!opp) return <div className="crm-content"><p>Opportunity not found</p></div>;

  const actionIcon = (type) => {
    switch (type) {
      case 'Called': return '📞';
      case 'Emailed': return '✉️';
      case 'Meeting Arranged': return '📅';
      case 'Meeting Held': return '🤝';
      case 'Quote Sent': return '📄';
      case 'Follow Up': return '🔔';
      case 'Status Changed': return '🔄';
      case 'Reassigned': return '👤';
      case 'Created': return '✨';
      default: return '📝';
    }
  };

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} backTo="/crm/opportunities" backLabel="Opportunities" />
      <div className="crm-content">
        {/* Header */}
        <div className="crm-detail-header">
          <div style={{ flex: 1 }}>
            <h2 className="crm-page-title">{opp.Title}</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              <span className="crm-status-badge" style={{ background: opp.StatusColor, color: '#fff' }}>{opp.StatusName}</span>
              {opp.IsRecurring && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(124,58,237,0.2)', color: '#a78bfa', fontWeight: 600 }}>🔄 Recurring</span>}
              {opp.IsTender && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(251,146,60,0.2)', color: '#fb923c', fontWeight: 600 }}>📋 Tender{opp.TenderEndDate ? ` · Ends ${new Date(opp.TenderEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</span>}
              {opp.CompanyName && (
                <span style={{ color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
                  onClick={() => navigate(`/crm/clients/${opp.CompanyCounter}`)}>🏢 {opp.CompanyName}</span>
              )}
              {opp.FORENAME && (
                <span style={{ color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
                  onClick={() => navigate(`/crm/contacts/${opp.ContactId}`)}>👤 {opp.FORENAME} {opp.SURNAME}</span>
              )}
              {opp.JobRef && <span style={{ color: C.textMuted, fontSize: 13 }}>📋 {opp.JobRef}</span>}
              {opp.Competitor && <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600 }}>⚔️ {opp.Competitor}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.green }}>{fmtMoney(opp.EstimatedValue)}</div>
            <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>
              Assigned to: <span className="crm-assignee-tag">{opp.AssignedName || opp.AssignedTo.split('@')[0]}</span>
            </div>
          </div>
        </div>

        {/* Status pipeline */}
        <div className="crm-opp-pipeline">
          {statuses.filter(s => s.IsActive).map(s => (
            <button key={s.Id} className={`crm-pipeline-step ${opp.StatusId === s.Id ? 'active' : ''}`}
              style={opp.StatusId === s.Id ? { background: s.Color, borderColor: s.Color, color: '#fff' } : {}}
              onClick={() => { if (s.Id !== opp.StatusId) changeStatus(s.Id); }}>
              {s.Name}
            </button>
          ))}
        </div>

        <div className="crm-opp-detail-grid">
          {/* Left: Info + Edit */}
          <div>
            <div className="crm-table-card" style={{ padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Details</h3>
                <button className="admin-sub-action" onClick={editing ? saveEdit : startEdit}>
                  {editing ? '💾 Save' : '✏️ Edit'}
                </button>
              </div>
              {editing ? (
                <div className="crm-create-grid">
                  <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Title</label>
                    <input type="text" value={editData.title} onChange={e => setEditData({ ...editData, title: e.target.value })} className="crm-input" />
                  </div>
                  <div className="crm-create-field">
                    <label>Client</label>
                    <div style={{ position: 'relative' }}>
                      <input type="text" value={editCompany} onChange={e => searchEditCompany(e.target.value)} className="crm-input" />
                      {editCompanySuggestions.length > 0 && (
                        <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                          {editCompanySuggestions.map(c => (
                            <div key={c.CompanyCounter} className="crm-search-item" onClick={() => {
                              setEditData({ ...editData, companyCounter: c.CompanyCounter });
                              setEditCompany(c.CompanyName);
                              setEditCompanySuggestions([]);
                            }}>
                              <span className="crm-search-item-title">{c.CompanyName}</span>
                            </div>
                          ))}
                          <div className="crm-search-item" style={{ borderTop: '1px solid rgba(148,163,184,0.2)', color: '#4ade80', fontWeight: 600 }}
                            onClick={async () => {
                              const name = editCompany.trim();
                              if (!name) return;
                              const res = await fetch('/api/crm/create-client', {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ companyName: name }),
                              });
                              const data = await res.json();
                              const cc = res.ok ? data.companyCounter : data.existingId;
                              if (cc) {
                                setEditData({ ...editData, companyCounter: cc });
                                setEditCompany(name);
                                setEditCompanySuggestions([]);
                              } else {
                                alert(data.error || 'Failed to create client');
                              }
                            }}>
                            <span className="crm-search-item-title">+ Create "{editCompany.trim()}"</span>
                            <span className="crm-search-item-sub">Add as new client</span>
                          </div>
                        </div>
                      )}
                      {editCompany.length >= 2 && editCompanySuggestions.length === 0 && !editData.companyCounter && (
                        <div className="crm-search-dropdown" style={{ top: '100%', width: '100%' }}>
                          <div className="crm-search-item" style={{ color: '#4ade80', fontWeight: 600 }}
                            onClick={async () => {
                              const name = editCompany.trim();
                              if (!name) return;
                              const res = await fetch('/api/crm/create-client', {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ companyName: name }),
                              });
                              const data = await res.json();
                              const cc = res.ok ? data.companyCounter : data.existingId;
                              if (cc) {
                                setEditData({ ...editData, companyCounter: cc });
                                setEditCompany(name);
                              } else {
                                alert(data.error || 'Failed to create client');
                              }
                            }}>
                            <span className="crm-search-item-title">+ Create "{editCompany.trim()}"</span>
                            <span className="crm-search-item-sub">No matches — add as new client</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="crm-create-field">
                    <label>HT Job Ref</label>
                    <input type="text" value={editData.jobRef} onChange={e => setEditData({ ...editData, jobRef: e.target.value })} placeholder="e.g. J12345" className="crm-input" />
                  </div>
                  <div className="crm-create-field">
                    <label>Estimated Value (£)</label>
                    <input type="number" value={editData.estimatedValue} onChange={e => setEditData({ ...editData, estimatedValue: e.target.value })} className="crm-input" />
                  </div>
                  <div className="crm-create-field">
                    <label>Expected Close Date</label>
                    <input type="date" value={editData.expectedCloseDate} onChange={e => setEditData({ ...editData, expectedCloseDate: e.target.value })} className="crm-input" />
                  </div>
                  <div className="crm-create-field">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={editData.isRecurring || false} onChange={e => setEditData({ ...editData, isRecurring: e.target.checked })} />
                      Recurring Project
                    </label>
                    {editData.isRecurring && (
                      <select value={editData.recurringIntervalDays || '30'} onChange={e => setEditData({ ...editData, recurringIntervalDays: e.target.value })} className="crm-input" style={{ marginTop: 6 }}>
                        <option value="7">Weekly</option>
                        <option value="14">Fortnightly</option>
                        <option value="30">Monthly</option>
                        <option value="60">Every 2 Months</option>
                        <option value="90">Quarterly</option>
                        <option value="182">Every 6 Months</option>
                        <option value="365">Yearly</option>
                      </select>
                    )}
                  </div>
                  <div className="crm-create-field">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={editData.isTender || false} onChange={e => setEditData({ ...editData, isTender: e.target.checked })} />
                      Tender
                    </label>
                    {editData.isTender && (
                      <div style={{ marginTop: 6 }}>
                        <label style={{ fontSize: 11, color: C.textDim }}>Tender End Date</label>
                        <input type="date" value={editData.tenderEndDate || ''} onChange={e => setEditData({ ...editData, tenderEndDate: e.target.value })} className="crm-input" />
                      </div>
                    )}
                  </div>
                  {canAccess(user, 'manager') && (
                    <div className="crm-create-field">
                      <label>Assign To</label>
                      <select value={editData.assignedTo} onChange={e => setEditData({ ...editData, assignedTo: e.target.value })} className="crm-input">
                        {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="crm-create-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} className="crm-input" rows={3} />
                  </div>
                  <div className="crm-create-field">
                    <label>Competitor</label>
                    <input type="text" value={editData.competitor} onChange={e => setEditData({ ...editData, competitor: e.target.value })} placeholder="Who else is quoting?" className="crm-input" />
                  </div>
                  <div className="crm-create-field">
                    <label>Lost Reason</label>
                    <input type="text" value={editData.lostReason} onChange={e => setEditData({ ...editData, lostReason: e.target.value })} placeholder="Why was it lost?" className="crm-input" />
                  </div>
                </div>
              ) : (
                <div className="crm-detail-fields">
                  {opp.Description && <div className="crm-detail-field"><span className="crm-field-label">Description</span><span>{opp.Description}</span></div>}
                  <div className="crm-detail-field"><span className="crm-field-label">Close Date</span><span>{opp.ExpectedCloseDate ? new Date(opp.ExpectedCloseDate).toLocaleDateString('en-GB') : 'Not set'}</span></div>
                  {opp.IsRecurring && (
                    <div className="crm-detail-field"><span className="crm-field-label">Recurring</span><span style={{ color: '#a78bfa' }}>🔄 Every {
                      { 7: 'week', 14: '2 weeks', 30: 'month', 60: '2 months', 90: 'quarter', 182: '6 months', 365: 'year' }[opp.RecurringIntervalDays] || `${opp.RecurringIntervalDays} days`
                    }</span></div>
                  )}
                  {opp.IsTender && (
                    <div className="crm-detail-field"><span className="crm-field-label">Tender</span><span style={{ color: '#fb923c' }}>📋 {opp.TenderEndDate ? `Ends ${new Date(opp.TenderEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'No end date set'}</span></div>
                  )}
                  <div className="crm-detail-field"><span className="crm-field-label">Created</span><span>{new Date(opp.CreatedAt).toLocaleDateString('en-GB')} by {opp.CreatedBy?.split('@')[0]}</span></div>
                  {opp.ClosedAt && <div className="crm-detail-field"><span className="crm-field-label">Closed</span><span>{new Date(opp.ClosedAt).toLocaleDateString('en-GB')}</span></div>}
                  {opp.Competitor && <div className="crm-detail-field"><span className="crm-field-label">Competitor</span><span style={{ color: '#ef4444' }}>⚔️ {opp.Competitor}</span></div>}
                  {opp.LostReason && <div className="crm-detail-field"><span className="crm-field-label">Lost Reason</span><span style={{ color: '#fb923c' }}>{opp.LostReason}</span></div>}
                </div>
              )}
            </div>
          </div>

          {/* Right: Actions */}
          <div>
            <div className="crm-table-card" style={{ padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
                Activity
              </h3>
              {/* Add action */}
              <div className="crm-action-form">
                <select value={actionType} onChange={e => setActionType(e.target.value)} className="crm-input" style={{ flex: '0 0 auto', width: 'auto' }}>
                  {ACTION_TYPES.filter(t => t !== 'Status Changed' && t !== 'Created').map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="text" value={actionNote} onChange={e => setActionNote(e.target.value)}
                  placeholder="Add a note..." className="crm-input" style={{ flex: 1 }}
                  onKeyDown={e => { if (e.key === 'Enter') addAction(); }} />
                <button className="crm-btn-primary" onClick={addAction} style={{ padding: '8px 14px' }}>Log</button>
              </div>

              {/* Action history */}
              <div className="crm-action-timeline">
                {(opp.actions || []).map(a => (
                  <div key={a.Id} className="crm-action-item">
                    <span className="crm-action-icon">{actionIcon(a.ActionType)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.ActionType}</div>
                      {a.Note && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{a.Note}</div>}
                      <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                        {a.CreatedByName || a.CreatedBy?.split('@')[0]} · {new Date(a.CreatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CrmTasks = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ active: 0, snoozed: 0, completed: 0, overdue: 0, dueToday: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterAssigned, setFilterAssigned] = useState(user?.email || '');
  const [search, setSearch] = useState('');
  const [salesUsers, setSalesUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [snoozeId, setSnoozeId] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editData, setEditData] = useState({});
  const [editCompany, setEditCompany] = useState('');
  const [editCompanySuggestions, setEditCompanySuggestions] = useState([]);
  const isManager = canAccess(user, 'manager');

  // Create form
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [newDue, setNewDue] = useState('');
  const [newAssignedTo, setNewAssignedTo] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const fetchTasks = useCallback(() => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (isManager && filterAssigned) params.set('assignedTo', filterAssigned);
    if (isManager && !filterAssigned) params.set('all', '1');
    fetch(`/api/crm/tasks?${params}`).then(r => r.ok ? r.json() : []).then(setTasks);
    const sumParams = isManager && filterAssigned ? `?assignedTo=${filterAssigned}` : '';
    fetch(`/api/crm/tasks/summary${sumParams}`).then(r => r.ok ? r.json() : {}).then(setSummary);
  }, [filterStatus, filterAssigned, isManager]);

  useEffect(() => {
    fetch('/api/crm/sales-users').then(r => r.ok ? r.json() : []).then(setSalesUsers);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Company autocomplete for create form
  useEffect(() => {
    if (newCompany.length < 2) { setCompanySuggestions([]); return; }
    const t = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(newCompany)}`).then(r => r.ok ? r.json() : {})
        .then(d => setCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
    return () => clearTimeout(t);
  }, [newCompany]);

  const createTask = async () => {
    if (!newTitle.trim()) return;
    await fetch('/api/crm/tasks', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: newTitle, description: newDesc, priority: newPriority,
        assignedTo: newAssignedTo || user.email,
        dueDate: newDue || null,
        companyCounter: selectedCompany?.CompanyCounter || null,
      }),
    });
    setNewTitle(''); setNewDesc(''); setNewPriority('normal'); setNewDue('');
    setNewAssignedTo(''); setNewCompany(''); setSelectedCompany(null);
    setShowCreate(false); fetchTasks();
  };

  const completeTask = async (id) => {
    await fetch(`/api/crm/tasks/${id}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    fetchTasks();
  };

  const reopenTask = async (id) => {
    await fetch(`/api/crm/tasks/${id}/reopen`, { method: 'POST' });
    fetchTasks();
  };

  const snoozeTask = async (id) => {
    if (!snoozeDate) return;
    await fetch(`/api/crm/tasks/${id}/snooze`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ until: snoozeDate }),
    });
    setSnoozeId(null); setSnoozeDate(''); fetchTasks();
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await fetch(`/api/crm/tasks/${id}`, { method: 'DELETE' });
    fetchTasks();
  };

  const startEditTask = (t) => {
    setEditingTask(t.Id);
    setEditData({
      title: t.Title || '', description: t.Description || '', priority: t.Priority || 'normal',
      dueDate: t.DueDate ? t.DueDate.split('T')[0] : '', assignedTo: t.AssignedTo || '',
      companyCounter: t.CompanyCounter || null,
    });
    setEditCompany(t.CompanyName || '');
    setEditCompanySuggestions([]);
  };

  const saveEditTask = async () => {
    if (!editingTask) return;
    await fetch(`/api/crm/tasks/${editingTask}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: editData.title, description: editData.description || null,
        priority: editData.priority, dueDate: editData.dueDate || null,
        assignedTo: editData.assignedTo || user.email,
        companyCounter: editData.companyCounter || null,
      }),
    });
    setEditingTask(null); setEditData({}); setEditCompany(''); fetchTasks();
  };

  // Edit company search
  useEffect(() => {
    if (!editingTask || editCompany.length < 2 || editData.companyCounter) { setEditCompanySuggestions([]); return; }
    const t = setTimeout(() => {
      fetch(`/api/crm/search?q=${encodeURIComponent(editCompany)}`).then(r => r.ok ? r.json() : {})
        .then(d => setEditCompanySuggestions((d.clients || []).slice(0, 5)));
    }, 300);
    return () => clearTimeout(t);
  }, [editCompany, editingTask, editData.companyCounter]);

  const PRIORITY_COLORS = { urgent: '#ef4444', high: '#fb923c', normal: '#38bdf8', low: '#64748b' };
  const PRIORITY_LABELS = { urgent: '🔴 Urgent', high: '🟠 High', normal: '🔵 Normal', low: '⚪ Low' };

  const filtered = tasks.filter(t => {
    if (search) {
      const s = search.toLowerCase();
      if (!(t.Title || '').toLowerCase().includes(s)
        && !(t.CompanyName || '').toLowerCase().includes(s)
        && !(t.ContactName || '').toLowerCase().includes(s)
        && !(t.Description || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const today = new Date().toISOString().split('T')[0];
  const getTaskAge = (t) => {
    if (!t.DueDate) return null;
    const due = t.DueDate.split('T')[0];
    if (due < today && t.Status === 'open') return 'overdue';
    if (due === today) return 'today';
    return null;
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getUserName = (email) => {
    const u = salesUsers.find(s => s.Email === email);
    return u ? u.DisplayName || email.split('@')[0] : email ? email.split('@')[0] : '—';
  };

  if (loading) return <><CrmTopBar user={user} onMenuToggle={onMenuToggle} /><div className="crm-content"><div className="loading-spinner" /></div></>;

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} />
      <div className="crm-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 className="crm-page-title">{isManager ? 'Team Tasks' : 'My Tasks'}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {canAccess(user, 'manager') && <button className="crm-btn-primary" onClick={() => {
              exportCSV(`tasks-${new Date().toISOString().split('T')[0]}.csv`,
                ['Title', 'Status', 'Priority', 'Type', 'Due Date', 'Company', 'Contact', 'Assigned To', 'Created'],
                filtered.map(t => [t.Title, t.Status, t.Priority, t.TaskType, t.DueDate ? new Date(t.DueDate).toLocaleDateString('en-GB') : '', t.CompanyName, t.ContactName, getUserName(t.AssignedTo), new Date(t.CreatedAt).toLocaleDateString('en-GB')])
              );
            }} style={{ fontSize: 12, padding: '6px 14px', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}>
              ↓ Export CSV
            </button>}
            <button className="crm-btn-primary" onClick={() => setShowCreate(!showCreate)}>+ New Task</button>
          </div>
        </div>

        {/* Summary KPIs */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Active', count: summary.active, color: '#38bdf8', status: 'open' },
            { label: 'Overdue', count: summary.overdue, color: '#ef4444', status: 'overdue' },
            { label: 'Due Today', count: summary.dueToday, color: '#fb923c', status: 'due_today' },
            { label: 'Snoozed', count: summary.snoozed, color: '#a78bfa', status: 'snoozed' },
            { label: 'Completed', count: summary.completed, color: '#4ade80', status: 'completed' },
          ].map(k => (
            <div key={k.label} onClick={() => setFilterStatus(k.status)}
              style={{
                padding: '12px 20px', borderRadius: 10,
                background: filterStatus === k.status
                  ? 'rgba(124,58,237,0.2)' : C.card,
                border: `1px solid ${filterStatus === k.status ? C.purple : C.cardBorder}`,
                cursor: 'pointer', minWidth: 100, textAlign: 'center',
              }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: k.color }}>{k.count || 0}</div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="Search tasks..." value={search}
            onChange={e => setSearch(e.target.value)} className="crm-search-input" style={{ maxWidth: 250 }} />
          <select className="crm-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="open">Active</option>
            <option value="overdue">Overdue</option>
            <option value="due_today">Due Today</option>
            <option value="snoozed">Snoozed</option>
            <option value="completed">Completed</option>
            <option value="">All</option>
          </select>
          {isManager && (
            <select className="crm-filter-select" value={filterAssigned} onChange={e => setFilterAssigned(e.target.value)}>
              <option value="">All Team</option>
              {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
            </select>
          )}
          <span style={{ color: C.textDim, fontSize: 12 }}>{filtered.length} tasks</span>
        </div>

        {/* Create Task Form */}
        {showCreate && (
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Create Task</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="crm-field-label">Title *</label>
                <input className="crm-input" value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  placeholder="e.g. Follow up with client about quote" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="crm-field-label">Description</label>
                <textarea className="crm-input" rows={2} value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  placeholder="Optional details..." />
              </div>
              <div>
                <label className="crm-field-label">Priority</label>
                <select className="crm-input" value={newPriority} onChange={e => setNewPriority(e.target.value)}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="crm-field-label">Due Date</label>
                <input type="date" className="crm-input" value={newDue} onChange={e => setNewDue(e.target.value)} />
              </div>
              {isManager && (
                <div>
                  <label className="crm-field-label">Assign To</label>
                  <select className="crm-input" value={newAssignedTo} onChange={e => setNewAssignedTo(e.target.value)}>
                    <option value="">Myself</option>
                    {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
                  </select>
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <label className="crm-field-label">Linked Client</label>
                <input className="crm-input" value={selectedCompany ? selectedCompany.CompanyName : newCompany}
                  onChange={e => { setNewCompany(e.target.value); setSelectedCompany(null); }}
                  placeholder="Start typing to search..." />
                {companySuggestions.length > 0 && !selectedCompany && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.card, border: `1px solid ${C.cardBorder}`,
                    borderRadius: 8, zIndex: 20, maxHeight: 150, overflow: 'auto' }}>
                    {companySuggestions.map(c => (
                      <div key={c.CompanyCounter} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                        onClick={() => { setSelectedCompany(c); setNewCompany(c.CompanyName); setCompanySuggestions([]); }}
                        onMouseEnter={e => e.target.style.background = 'rgba(124,58,237,0.15)'}
                        onMouseLeave={e => e.target.style.background = ''}>
                        {c.CompanyName} <span style={{ color: C.textDim, fontSize: 11 }}>{c.Town || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="crm-btn-primary" onClick={createTask}>Create Task</button>
              <button onClick={() => setShowCreate(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Task List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: C.textDim }}>
              {filterStatus === 'open' ? 'No active tasks — nice work!' : 'No tasks found'}
            </div>
          )}
          {filtered.map(t => {
            const age = getTaskAge(t);
            const isSnoozed = t.SnoozedUntil && t.SnoozedUntil.split('T')[0] > today && t.Status === 'open';

            // Edit mode
            if (editingTask === t.Id) return (
              <div key={t.Id} style={{ background: C.card, border: `1px solid ${C.purple}`, borderRadius: 10, padding: 18 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Edit Task</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="crm-field-label">Title</label>
                    <input className="crm-input" value={editData.title} onChange={e => setEditData({ ...editData, title: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="crm-field-label">Description</label>
                    <textarea className="crm-input" rows={2} value={editData.description} onChange={e => setEditData({ ...editData, description: e.target.value })} />
                  </div>
                  <div>
                    <label className="crm-field-label">Priority</label>
                    <select className="crm-input" value={editData.priority} onChange={e => setEditData({ ...editData, priority: e.target.value })}>
                      <option value="low">Low</option><option value="normal">Normal</option>
                      <option value="high">High</option><option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="crm-field-label">Due Date</label>
                    <input type="date" className="crm-input" value={editData.dueDate} onChange={e => setEditData({ ...editData, dueDate: e.target.value })} />
                  </div>
                  <div>
                    <label className="crm-field-label">Assign To</label>
                    <select className="crm-input" value={editData.assignedTo} onChange={e => setEditData({ ...editData, assignedTo: e.target.value })}>
                      {salesUsers.map(u => <option key={u.Email} value={u.Email}>{u.DisplayName || u.Email}</option>)}
                    </select>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <label className="crm-field-label">Linked Client</label>
                    <input className="crm-input" value={editData.companyCounter ? editCompany : editCompany}
                      onChange={e => { setEditCompany(e.target.value); setEditData(prev => ({ ...prev, companyCounter: null })); }}
                      placeholder="Search client..." />
                    {editData.companyCounter && editCompany && (
                      <button onClick={() => { setEditData(prev => ({ ...prev, companyCounter: null })); setEditCompany(''); }}
                        style={{ position: 'absolute', right: 8, top: 24, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14 }}>✕</button>
                    )}
                    {editCompanySuggestions.length > 0 && !editData.companyCounter && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.card, border: `1px solid ${C.cardBorder}`,
                        borderRadius: 8, zIndex: 20, maxHeight: 150, overflow: 'auto' }}>
                        {editCompanySuggestions.map(c => (
                          <div key={c.CompanyCounter} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}
                            onClick={() => { setEditData(prev => ({ ...prev, companyCounter: c.CompanyCounter })); setEditCompany(c.CompanyName); setEditCompanySuggestions([]); }}
                            onMouseEnter={e => e.target.style.background = 'rgba(124,58,237,0.15)'}
                            onMouseLeave={e => e.target.style.background = ''}>
                            {c.CompanyName} <span style={{ color: C.textDim, fontSize: 11 }}>{c.Town || ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="crm-btn-primary" onClick={saveEditTask}>Save</button>
                  <button onClick={() => { setEditingTask(null); setEditData({}); setEditCompany(''); }}
                    style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.cardBorder}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
              </div>
            );

            return (
              <div key={t.Id} style={{
                background: C.card, border: `1px solid ${age === 'overdue' ? '#ef4444' : age === 'today' ? '#fb923c' : C.cardBorder}`,
                borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 14,
                opacity: t.Status === 'completed' ? 0.6 : isSnoozed ? 0.7 : 1,
              }}>
                {/* Checkbox */}
                <div onClick={() => t.Status === 'completed' ? reopenTask(t.Id) : completeTask(t.Id)}
                  style={{
                    width: 22, height: 22, borderRadius: 6, border: `2px solid ${t.Status === 'completed' ? '#4ade80' : C.cardBorder}`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2,
                    background: t.Status === 'completed' ? '#4ade80' : 'transparent',
                  }}>
                  {t.Status === 'completed' && <span style={{ color: '#000', fontSize: 14, fontWeight: 700 }}>✓</span>}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontWeight: 600, fontSize: 14,
                      textDecoration: t.Status === 'completed' ? 'line-through' : 'none',
                      color: t.Status === 'completed' ? C.textDim : C.text,
                    }}>{t.Title}</span>
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                      background: PRIORITY_COLORS[t.Priority] + '22', color: PRIORITY_COLORS[t.Priority],
                    }}>{t.Priority.toUpperCase()}</span>
                    {t.TaskType !== 'manual' && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(124,58,237,0.2)', color: '#a78bfa' }}>
                        {t.TaskType === 'contact_due' ? 'AUTO: Contact Due' : t.TaskType === 'recurring_opp' ? 'AUTO: Recurring' : t.TaskType}
                      </span>
                    )}
                    {age === 'overdue' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.2)', color: '#ef4444', fontWeight: 600 }}>OVERDUE</span>}
                    {age === 'today' && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(251,146,60,0.2)', color: '#fb923c', fontWeight: 600 }}>DUE TODAY</span>}
                    {isSnoozed && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(167,139,250,0.2)', color: '#a78bfa' }}>Snoozed until {fmtDate(t.SnoozedUntil)}</span>}
                  </div>
                  {t.Description && <div style={{ fontSize: 12, color: C.textDim, marginTop: 4, lineHeight: 1.4 }}>{t.Description}</div>}
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: C.textMuted, flexWrap: 'wrap' }}>
                    {t.DueDate && <span>📅 {fmtDate(t.DueDate)}</span>}
                    {t.CompanyName && (
                      <span style={{ cursor: 'pointer', color: '#a78bfa' }}
                        onClick={() => navigate(`/crm/clients/${t.CompanyCounter}`)}>🏢 {t.CompanyName}</span>
                    )}
                    {t.ContactName && t.ContactName.trim() && (
                      <span style={{ cursor: 'pointer', color: '#a78bfa' }}
                        onClick={() => navigate(`/crm/contacts/${t.ContactId}`)}>👤 {t.ContactName}</span>
                    )}
                    {t.OpportunityTitle && <span>💰 {t.OpportunityTitle}</span>}
                    {isManager && <span>👤 {getUserName(t.AssignedTo)}</span>}
                    {t.CompletedAt && <span>✓ Completed {fmtDate(t.CompletedAt)}</span>}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {t.Status === 'open' && snoozeId === t.Id ? (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input type="date" className="crm-input" style={{ fontSize: 11, padding: '2px 6px', width: 130 }}
                        value={snoozeDate} onChange={e => setSnoozeDate(e.target.value)} min={today}
                        onKeyDown={e => { if (e.key === 'Enter') snoozeTask(t.Id); if (e.key === 'Escape') { setSnoozeId(null); setSnoozeDate(''); } }}
                        autoFocus />
                      <button className="crm-btn-small" onClick={() => snoozeTask(t.Id)}
                        style={{ fontSize: 11, padding: '2px 8px' }}>Go</button>
                      <button className="crm-btn-small" onClick={() => { setSnoozeId(null); setSnoozeDate(''); }}
                        style={{ fontSize: 11, padding: '2px 8px' }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <button className="crm-btn-small" title="Edit" onClick={() => startEditTask(t)}
                        style={{ fontSize: 13, padding: '2px 8px' }}>✏️</button>
                      {t.Status === 'open' && <>
                        <button className="crm-btn-small" title="Snooze" onClick={() => setSnoozeId(t.Id)}
                          style={{ fontSize: 13, padding: '2px 8px' }}>💤</button>
                        <button className="crm-btn-small" title="Delete" onClick={() => deleteTask(t.Id)}
                          style={{ fontSize: 13, padding: '2px 8px', color: '#ef4444' }}>🗑</button>
                      </>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// HOW TO GUIDE
// ══════════════════════════════════════════════

const guideContent = {
  everyone: [
    {
      title: '📊 Dashboard',
      sections: [
        { heading: 'Overview', text: 'Your dashboard greets you with an actionable summary: overdue tasks, tasks due today, contacts to reach out to, and stale opportunities. Clickable badges take you straight to the relevant page. If everything\'s clear, you\'ll see a green "all caught up" badge.' },
        { heading: 'Quick Log from Dashboard', text: 'In the Contact Reminders section, use the 📞 ✉️ 🤝 buttons to log a call, email, or meeting without leaving the dashboard. This logs the activity, resets the contact frequency timer, and removes the reminder from the list.' },
        { heading: 'Charts', text: 'Click on any chart segment (spend by AM, opportunities by stage, etc.) to drill down and see the underlying records. Click the drill-down row to navigate directly to that client or opportunity.' },
        { heading: 'Pipeline Summary', text: 'The pipeline section shows your open opportunities grouped by stage, with total values. Use this to get a quick read on your sales funnel.' },
        { heading: 'Sales Leaderboard', text: 'The dashboard shows a live leaderboard ranking all salespeople by confirmed revenue for the year. It compares each person\'s confirmed income against their YTD adjusted budget, showing variance (green = ahead, red = behind), a progress bar, and percentage of full-year target. Data is pulled from the same budget figures used on the Sales Forecast page.' },
        { heading: 'Win/Loss Analytics', text: 'The dashboard shows win/loss statistics for all closed opportunities: total won vs lost, win rate percentage, average deal size, average days to close, and a breakdown by person. Use this to identify trends and improve conversion rates.' },
        { heading: 'Stale Pipeline Alerts', text: 'The dashboard highlights open opportunities that haven\'t had any activity logged for 7+ days. They\'re colour-coded by urgency: yellow (7–13 days idle), amber (14–29 days), red (30+ days). Click any alert to go to the opportunity and take action.' },
      ]
    },
    {
      title: '📊 Sales Forecast',
      sections: [
        { heading: 'Overview', text: 'The Sales Forecast page shows projected revenue based on your opportunities. It pulls weighted values (value × probability) to give a realistic forecast.' },
      ]
    },
    {
      title: '🏢 My Clients',
      sections: [
        { heading: 'Finding Clients', text: 'Use the search bar to find clients by name, town, phone, email, account reference, or last job. The list defaults to showing your assigned clients — use the view toggle to switch between "Mine", "All", and "Unassigned".' },
        { heading: 'Sorting', text: 'Click any column header to sort. Click again to reverse the sort direction. The default sort is by Spend YTD (highest first).' },
        { heading: 'Client Detail', text: 'Click any client row to open their detail page. Here you can see their full info, contacts, opportunities, activity log, subscription tags, and contact frequency settings.' },
        { heading: 'Star Ratings', text: 'Rate clients 1-5 stars to prioritise your accounts. Ratings appear in the table and can help you quickly identify key accounts.' },
        { heading: 'Subscription Tags', text: 'Tags like "Open Account", "Hire Account", etc. help categorise clients. Toggle them on the client detail page, or use bulk actions to tag multiple clients at once.' },
        { heading: 'Contact Frequency', text: 'Set how often you want to contact a client (weekly, fortnightly, monthly, quarterly, etc.) from their detail page. The system will auto-generate tasks when you\'re due to reach out.' },
        { heading: 'Activity Log', text: 'On each client detail page, log calls, emails, meetings, and notes. These are timestamped and shown as a timeline. Logging activity also resets the contact frequency timer. Completing a task linked to a client also appears in the activity feed as a ✅ Task Completed entry.' },
        { heading: 'Follow-up Reminders', text: 'When logging an activity, tick "Set follow-up reminder" and choose a date. This creates a task linked to the client with the subject as the title, so you don\'t forget to follow up.' },
        { heading: 'Pinned Notes', text: 'Add pinned notes to any client — they show as yellow cards at the top of the detail page, visible to everyone. Use them for key information like "Payment terms: 60 days" or "Main contact is on maternity leave until June". You can edit or delete notes anytime.' },
        { heading: 'HireTrack Notes', text: 'Notes from HireTrack are synced and displayed on the client overview page under "📋 HireTrack Notes". These are pulled automatically during the hourly sync.' },
        { heading: 'Editing Clients', text: 'Click the ✏️ Edit button on any client detail page to edit company name, phone, email, mobile, fax, website, VAT number, accounts details, and full address. Saving pushes changes to both the CRM and HireTrack simultaneously.' },
        { heading: 'Client Health Score', text: 'Each client has a health score (0–100) shown on their detail page. It\'s auto-calculated from: recent activity (30pts), contact frequency compliance (20pts), star rating (20pts), open opportunities (15pts), and number of contacts (15pts). Scores are graded as Excellent (80+), Good (60+), Needs Attention (40+), or At Risk. Use this to spot clients that need more engagement.' },
        { heading: 'Last Contacted', text: 'The clients list has a "Last Contact" column showing how many days since the last logged activity. Colour-coded: green (within 7 days), amber (within 30 days), red (over 30 days). Click the column header to sort and quickly find neglected accounts.' },
        { heading: 'Creating Clients', text: 'Click "+ New Client" to create a new company. This writes to both HireTrack and the CRM simultaneously. Company name is required.' },
      ]
    },
    {
      title: '👤 My Contacts',
      sections: [
        { heading: 'Finding Contacts', text: 'Search by name, email, company, or job title. Filter by subscription tag using the tag buttons above the grid.' },
        { heading: 'Contact Cards', text: 'Each contact shows their name, title, job title, company, email, and phone. Click a card to open their detail page with full info, activity log, and subscription management.' },
        { heading: 'Creating Contacts', text: 'Click "+ New Contact" and fill in the details. Search for a company to link them to. First and last name are required.' },
        { heading: 'Editing Contacts', text: 'Click the ✏️ Edit button on any contact detail page to edit their name, title, salutation, job title, nickname, email, phone, mobile, and full address. Saving pushes changes to both the CRM and HireTrack simultaneously.' },
        { heading: 'Job Title', text: 'Each contact has a Job Title field (separate from the HireTrack Title/salutation). You can quick-edit it by clicking on it directly, or edit it as part of the full Edit form. Job titles are searchable and shown on contact cards.' },
        { heading: 'Subscription Tags', text: 'Toggle tags directly on each contact card. Tags are shared across the CRM so everyone sees the same categorisation.' },
      ]
    },
    {
      title: '💰 Opportunities',
      sections: [
        { heading: 'Pipeline View', text: 'The opportunities page shows your deals as a kanban board by default. Cards are grouped into columns: Prospect → Quoted → Negotiation → Won / Lost. Your view preference (board or list) is remembered across navigation.' },
        { heading: 'List View', text: 'Toggle between kanban and list view using the buttons at the top. List view gives a sortable table with all opportunity fields.' },
        { heading: 'Drag & Drop', text: 'In kanban view, drag opportunity cards between columns to update their stage. The system auto-records the stage change in the activity feed.' },
        { heading: 'Creating Opportunities', text: 'Click "+ New Opportunity" and fill in the details: title, company, value, stage, probability, expected close date, and optional description. Link it to a client to track against their account. You can also create a new client directly from the company search if they don\'t exist yet.' },
        { heading: 'Tender Flag', text: 'Mark an opportunity as a Tender and set a tender end date for tracking 3-year tender cycles. Tender opportunities show an orange 📋 badge with the end date on both list and kanban views.' },
        { heading: 'Competitor Tracking', text: 'On any opportunity, click Edit to record who you\'re competing against in the Competitor field. If a deal is lost, add a Lost Reason to capture why. Both fields appear on the opportunity detail page and are available in the win/loss analytics on the dashboard.' },
        { heading: 'Reassignment & Status Tracking', text: 'When an opportunity is reassigned or its status changes, the system automatically logs it in the activity feed — showing who it was reassigned from/to, or what the new status is.' },
        { heading: 'Recurring Opportunities', text: 'When creating or editing an opportunity, set a recurrence (monthly, quarterly, annual). When a recurring opportunity is won, the system auto-creates a follow-up opportunity and task for the next cycle.' },
        { heading: 'Opportunity Detail', text: 'Click any opportunity to see its full details, edit fields, log activity, and track its history.' },
      ]
    },
    {
      title: '☑ Tasks',
      sections: [
        { heading: 'Task List', text: 'Tasks are shown in tabs: Active, Due Today, Overdue, Completed, and Snoozed. The sidebar badge shows your total active + overdue count.' },
        { heading: 'Creating Tasks', text: 'Click "+ New Task" and fill in title, description, priority (low/normal/high/urgent), due date, and optionally link to a company or contact.' },
        { heading: 'Editing Tasks', text: 'Click the ✏️ button on any task to edit its title, description, priority, due date, assigned person, and linked client. Available on both open and completed tasks.' },
        { heading: 'Auto-Generated Tasks', text: 'The system auto-creates tasks for: contact frequency check-ins (when you\'re due to reach out to a client) and recurring opportunity follow-ups.' },
        { heading: 'Completing Tasks', text: 'Click the checkbox to complete a task. This logs a ✅ Task Completed entry in the linked client\'s activity feed. For contact frequency tasks, it also resets the contact timer.' },
        { heading: 'Snoozing', text: 'Click the 💤 icon to snooze a task. Pick a date from the date picker, then press Enter or click Go. Press Escape to cancel. The task moves to the Snoozed tab and reappears when the snooze expires.' },
        { heading: 'Priority Colours', text: 'Tasks are colour-coded by priority: blue (low), amber (medium), red (high), purple (urgent). The left border of each task card shows its priority.' },
      ]
    },
    {
      title: '🔔 Notifications',
      sections: [
        { heading: 'Bell Icon', text: 'The bell icon in the top bar shows your unread notification count. Click it to see your latest 30 notifications.' },
        { heading: 'View All', text: 'Click "View All Notifications" at the bottom of the dropdown to open a full-page view of all your notifications. Filter between All and Unread, and mark all as read in one click.' },
        { heading: 'Auto Notifications', text: 'You\'ll get notified when: a contact frequency task is created for you, a recurring opportunity follow-up is generated, a manager assigns a task to you, a client is assigned to you, an opportunity\'s status is changed by someone else, an opportunity is assigned to you, or someone logs activity on one of your clients.' },
        { heading: 'Clicking Notifications', text: 'Click a notification to mark it as read and jump to the relevant page. Contact due notifications go to the client or contact page. Recurring opportunity notifications go to the opportunity. Task notifications go to the tasks page.' },
        { heading: 'Mark All Read', text: 'Use the "Mark all read" button in the notification dropdown or on the full notifications page to clear all unread notifications at once.' },
        { heading: 'Daily Digest Email', text: 'Every morning at 7am you\'ll receive an email summary of your overdue tasks, tasks due today, and unread notifications. This only sends if you have outstanding items.' },
      ]
    },
    {
      title: '📧 Gmail Add-on',
      sections: [
        { heading: 'What It Does', text: 'The TSL CRM Gmail Add-on adds a sidebar panel to Gmail. When you open any email, it auto-matches the sender and recipients to CRM contacts and companies, and lets you log the email with one click.' },
        { heading: 'Logging an Email', text: 'Open an email in Gmail and click on the TSL CRM panel in the right sidebar. If the email addresses match a CRM contact or company, you\'ll see "Log to Contact" or "Log to Company" buttons. Click one to log the email — subject and full body are saved to the activity log.' },
        { heading: 'Manual Search', text: 'If no auto-match is found, use the search box in the sidebar to find the company by name. Click "Log Here" on the result to log the email.' },
        { heading: 'Follow-up Reminders', text: 'When logging from the sidebar, you can optionally set a follow-up date. This creates a task linked to the client, just like the follow-up reminder on the regular activity log.' },
        { heading: 'Duplicate Detection', text: 'The add-on tracks which emails have been logged using the Gmail message ID. If you try to log the same email twice, it\'ll tell you it\'s already been logged.' },
      ]
    },
    {
      title: '🔍 Global Search',
      sections: [
        { heading: 'How to Search', text: 'Use the search bar in the top navigation to search across all clients, contacts, jobs, and opportunities at once. Results appear in a dropdown grouped by type.' },
        { heading: 'Quick Navigation', text: 'Click any search result to jump straight to the relevant page. Client results go to the client detail overview. Job results go to the client detail Jobs tab. Contact and opportunity results go to their respective detail pages.' },
      ]
    },
  ],
  manager: [
    {
      title: '👥 Manager Features',
      sections: [
        { heading: 'Team View (Tasks)', text: 'On the Tasks page, managers can see a "Team" tab showing all tasks across the team, not just their own. Use this to monitor workload and ensure nothing falls through the cracks.' },
        { heading: 'Assigning Clients', text: 'On the Clients page, switch to "Unassigned" view to see clients not yet assigned to an account manager. Use the assign button or bulk select + assign to distribute accounts.' },
        { heading: 'Assigning Tasks', text: 'When creating a task, managers can assign it to any team member. The assignee will receive a notification.' },
        { heading: 'Bulk Actions (Clients)', text: 'Select multiple clients using checkboxes, then use the purple action bar to: assign to a team member, set star rating, add/remove subscription tags. Actions apply to all selected clients at once.' },
        { heading: 'Bulk Actions (Contacts)', text: 'Select multiple contacts using checkboxes, then add or remove subscription tags in bulk.' },
        { heading: 'CSV Export', text: 'Export buttons on Clients, Contacts, Opportunities, and Tasks pages let you download data as CSV files. This is restricted to manager+ roles.' },
        { heading: 'Review Clients', text: 'The Review Clients page shows all clients in a format designed for account review meetings — with spend data, ratings, and assignment info.' },
        { heading: 'Data Quality', text: 'The Data Quality page shows completeness scores for your company and contact data. It highlights missing fields (phone, email, town, postcode, job title) prioritised by client spend, and detects potential duplicate companies. Use this to systematically clean up your data.' },
      ]
    },
  ],
  admin: [
    {
      title: '⚙️ Admin Features',
      sections: [
        { heading: 'Admin Panel', text: 'Access from the sidebar (admin only). Manage user roles, run data syncs, generate auto-tasks, send test digest emails, and manage subscription types.' },
        { heading: 'User Roles', text: 'Four roles in hierarchy: Admin → Director → Manager → Sales Team. Higher roles can see and do everything lower roles can. Assign roles in the admin panel.' },
        { heading: 'Sync Data', text: 'The "Sync Now" button triggers a manual sync from HireTrack to the CRM database. This normally runs automatically on a schedule.' },
        { heading: 'Generate Auto Tasks', text: 'Click "Generate Auto Tasks" to create contact frequency tasks for any clients that are due or overdue for contact. This also runs automatically when the Tasks page loads.' },
        { heading: 'Send Daily Digest', text: 'Click "Send Daily Digest" to manually trigger the digest email to all users with outstanding tasks. Useful for testing. The digest normally sends automatically at 7am.' },
        { heading: 'Team Digest', text: 'Directors, managers, and admins receive a daily team breakdown email at 7:15am. It shows a table of every team member with their overdue tasks, tasks due today, active tasks, activities logged in the last 24 hours, open pipeline value, overdue contact frequencies, and stale opportunities. Anyone with 3+ issues is flagged in a "Needs Attention" section. Click "Send Team Digest" in the admin panel to trigger it manually.' },
        { heading: 'Subscription Types', text: 'Create and manage subscription tag types (e.g. "Open Account", "Hire Account", "Key Contact"). These appear across the CRM for tagging clients and contacts.' },
        { heading: 'Soft Delete', text: 'When clients or contacts are archived in HireTrack, they\'re soft-deleted in the CRM — hidden from lists but data is preserved. No data is permanently lost.' },
        { heading: 'Seed Test Data', text: 'Click "Seed Test Data" in the admin panel to populate a user\'s account with sample data for testing. Enter their email and display name. It assigns 12 clients, creates 8 opportunities across all pipeline stages, 11 tasks (mix of overdue, due today, and future), activity logs, notifications, contact frequencies, and star ratings. Safe to re-run — it cleans up previous seed data first.' },
      ]
    },
  ],
};

const CrmGuide = ({ user, onMenuToggle }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const role = user?.role || 'sales_team';
  const isManager = canAccess(user, 'manager');
  const isAdmin = canAccess(user, 'admin');

  const allSections = [
    ...guideContent.everyone,
    ...(isManager ? guideContent.manager : []),
    ...(isAdmin ? guideContent.admin : []),
  ];

  const toggle = (idx) => setExpandedSection(prev => prev === idx ? null : idx);

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} />
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">How to Guide</h2>
            <p className="crm-page-subtitle">Everything you need to know about using the TSL Sales CRM{isManager ? ' · Manager features included' : ''}{isAdmin ? ' + Admin' : ''}</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {allSections.map((section, idx) => (
            <div key={idx} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, overflow: 'hidden' }}>
              <button onClick={() => toggle(idx)} style={{
                width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: C.text,
              }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{section.title}</span>
                <span style={{ fontSize: 18, color: C.textMuted, transform: expandedSection === idx ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▾</span>
              </button>
              {expandedSection === idx && (
                <div style={{ padding: '0 20px 20px', borderTop: `1px solid ${C.cardBorder}` }}>
                  {section.sections.map((s, si) => (
                    <div key={si} style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.purpleLight, marginBottom: 4 }}>{s.heading}</div>
                      <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{s.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 24, padding: 16, background: 'rgba(124,58,237,0.08)', borderRadius: 10, border: `1px solid rgba(124,58,237,0.2)` }}>
          <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>
            💡 <strong style={{ color: C.purpleLight }}>Need help?</strong> Contact your CRM administrator or raise a request with the IT team.
          </p>
        </div>
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// DATA QUALITY
// ══════════════════════════════════════════════

const CrmDataQuality = ({ user, onMenuToggle }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    fetch('/api/crm/data-quality').then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); });
  }, []);

  if (loading) return <><CrmTopBar user={user} onMenuToggle={onMenuToggle} /><div className="crm-content"><Loading /></div></>;
  if (!data) return <><CrmTopBar user={user} onMenuToggle={onMenuToggle} /><div className="crm-content"><p>Failed to load data quality report</p></div></>;

  const { companies: cs, contacts: cn, incompleteCompanies, incompleteContacts, duplicates } = data;

  const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

  const StatBar = ({ label, value, total, color }) => {
    const p = pct(value, total);
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: C.text }}>{label}</span>
          <span style={{ color: C.textMuted }}>{value.toLocaleString()} / {total.toLocaleString()} ({p}%)</span>
        </div>
        <div style={{ background: '#0f1117', borderRadius: 4, height: 8, overflow: 'hidden' }}>
          <div style={{ width: `${p}%`, height: '100%', background: color || (p > 80 ? '#4ade80' : p > 50 ? '#fb923c' : '#ef4444'), borderRadius: 4, transition: 'width 0.5s ease' }} />
        </div>
      </div>
    );
  };

  const overallCompanyScore = cs.total > 0 ? Math.round(((cs.hasPhone + cs.hasEmail + cs.hasTown + cs.hasPostcode + cs.hasContacts) / (cs.total * 5)) * 100) : 0;
  const overallContactScore = cn.total > 0 ? Math.round(((cn.hasEmail + cn.hasMobile + cn.hasTitle + cn.hasCompany) / (cn.total * 4)) * 100) : 0;

  const missingLabels = (c) => {
    const m = [];
    if (!c.MainTelephone) m.push('Phone');
    if (!c.EmailAddress) m.push('Email');
    if (!c.Town) m.push('Town');
    if (!c.PostCode) m.push('Postcode');
    if (c.ContactCount === 0) m.push('Contacts');
    return m;
  };

  const contactMissingLabels = (c) => {
    const m = [];
    if (!c.EMAIL) m.push('Email');
    if (!c.Mobile && !c.Telephone) m.push('Phone');
    if (!c.TITLE) m.push('Job Title');
    return m;
  };

  const tabs = [
    { key: 'overview', label: '📊 Overview' },
    { key: 'companies', label: `🏢 Companies (${incompleteCompanies.length})` },
    { key: 'contacts', label: `👤 Contacts (${incompleteContacts.length})` },
    { key: 'duplicates', label: `🔄 Duplicates (${duplicates.length})` },
  ];

  return (
    <>
      <CrmTopBar user={user} onMenuToggle={onMenuToggle} />
      <div className="crm-content">
        <div className="crm-page-header">
          <div>
            <h2 className="crm-page-title">Data Quality</h2>
            <p className="crm-page-subtitle">Completeness scoring, missing fields, and duplicate detection</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${tab === t.key ? C.purple : C.cardBorder}`,
                background: tab === t.key ? C.purple : 'transparent', color: tab === t.key ? '#fff' : C.textMuted,
                cursor: 'pointer', fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
              }}>{t.label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            {/* Score cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Company Score', score: overallCompanyScore, color: overallCompanyScore > 80 ? '#4ade80' : overallCompanyScore > 50 ? '#fb923c' : '#ef4444' },
                { label: 'Contact Score', score: overallContactScore, color: overallContactScore > 80 ? '#4ade80' : overallContactScore > 50 ? '#fb923c' : '#ef4444' },
                { label: 'Incomplete Companies', score: incompleteCompanies.length, isCount: true, color: incompleteCompanies.length > 100 ? '#ef4444' : '#fb923c' },
                { label: 'Potential Duplicates', score: duplicates.length, isCount: true, color: duplicates.length > 0 ? '#fb923c' : '#4ade80' },
              ].map((card, i) => (
                <div key={i} className="crm-stat-card" style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: card.color }}>{card.isCount ? card.score : `${card.score}%`}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{card.label}</div>
                </div>
              ))}
            </div>

            {/* Company completeness */}
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>🏢 Company Completeness ({cs.total.toLocaleString()} active)</h3>
              <StatBar label="Has Phone Number" value={cs.hasPhone} total={cs.total} />
              <StatBar label="Has Email" value={cs.hasEmail} total={cs.total} />
              <StatBar label="Has Town" value={cs.hasTown} total={cs.total} />
              <StatBar label="Has Postcode" value={cs.hasPostcode} total={cs.total} />
              <StatBar label="Has Address" value={cs.hasAddress} total={cs.total} />
              <StatBar label="Has Contacts" value={cs.hasContacts} total={cs.total} />
              <StatBar label="Has AM Assigned" value={cs.hasAssignment} total={cs.total} color={C.purple} />
            </div>

            {/* Contact completeness */}
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>👤 Contact Completeness ({cn.total.toLocaleString()} active)</h3>
              <StatBar label="Has Email" value={cn.hasEmail} total={cn.total} />
              <StatBar label="Has Mobile" value={cn.hasMobile} total={cn.total} />
              <StatBar label="Has Any Phone" value={cn.hasPhone} total={cn.total} />
              <StatBar label="Has Job Title" value={cn.hasTitle} total={cn.total} />
              <StatBar label="Linked to Company" value={cn.hasCompany} total={cn.total} />
            </div>
          </>
        )}

        {tab === 'companies' && (
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.cardBorder}` }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Incomplete Companies</h3>
              <p style={{ fontSize: 12, color: C.textDim, margin: '4px 0 0' }}>Sorted by spend — fix the highest-value clients first</p>
            </div>
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th className="crm-th" style={{ textAlign: 'left' }}>COMPANY</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>SPEND YTD</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>MISSING</th>
                  </tr>
                </thead>
                <tbody>
                  {incompleteCompanies.map(c => (
                    <tr key={c.CompanyCounter} className="crm-tr" style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                      <td className="crm-td">
                        <div style={{ fontWeight: 600 }}>{c.CompanyName}</div>
                        <div className="crm-td-sub">{c.Town || 'No town'}</div>
                      </td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.SpendYTD)}</td>
                      <td className="crm-td">
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {missingLabels(c).map(m => (
                            <span key={m} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>{m}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'contacts' && (
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.cardBorder}` }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Incomplete Contacts</h3>
              <p style={{ fontSize: 12, color: C.textDim, margin: '4px 0 0' }}>Sorted by company spend — prioritise high-value relationships</p>
            </div>
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th className="crm-th" style={{ textAlign: 'left' }}>CONTACT</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>COMPANY</th>
                    <th className="crm-th" style={{ textAlign: 'right' }}>SPEND YTD</th>
                    <th className="crm-th" style={{ textAlign: 'left' }}>MISSING</th>
                  </tr>
                </thead>
                <tbody>
                  {incompleteContacts.map(c => (
                    <tr key={c.NameCounter} className="crm-tr" style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/crm/contacts/${c.NameCounter}`)}>
                      <td className="crm-td">
                        <div style={{ fontWeight: 600 }}>{c.FORENAME} {c.SURNAME}</div>
                        <div className="crm-td-sub">{c.TITLE || 'No title'}</div>
                      </td>
                      <td className="crm-td">{c.CompanyName || '—'}</td>
                      <td className="crm-td" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.SpendYTD)}</td>
                      <td className="crm-td">
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {contactMissingLabels(c).map(m => (
                            <span key={m} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>{m}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'duplicates' && (
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.cardBorder}` }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Potential Duplicate Companies</h3>
              <p style={{ fontSize: 12, color: C.textDim, margin: '4px 0 0' }}>Companies with similar names after removing "Ltd" / "Limited" / "The"</p>
            </div>
            {duplicates.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: C.textDim }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <p>No duplicates detected</p>
              </div>
            ) : (
              <div className="crm-table-wrap">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th className="crm-th" style={{ textAlign: 'left' }}>COMPANY A</th>
                      <th className="crm-th" style={{ textAlign: 'left' }}>COMPANY B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicates.map((d, i) => (
                      <tr key={i} className="crm-tr">
                        <td className="crm-td" style={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/clients/${d.Id1}`)}>
                          <div style={{ fontWeight: 600, color: C.purpleLight }}>{d.Name1}</div>
                          <div className="crm-td-sub">{d.Town1 || 'No town'}</div>
                        </td>
                        <td className="crm-td" style={{ cursor: 'pointer' }} onClick={() => navigate(`/crm/clients/${d.Id2}`)}>
                          <div style={{ fontWeight: 600, color: C.purpleLight }}>{d.Name2}</div>
                          <div className="crm-td-sub">{d.Town2 || 'No town'}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ══════════════════════════════════════════════
// CRM LAYOUT (exported)
// ══════════════════════════════════════════════

export default function CrmLayout({ user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(o => !o);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="crm-app">
      <CrmNav user={user} mobileOpen={sidebarOpen} onClose={closeSidebar} />
      <div className="crm-main">
        <Routes>
          <Route path="/" element={<CrmDashboard user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/sales" element={<SalesDashboard user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/clients/:id" element={<CrmClientDetail user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/clients" element={<CrmClients user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/contacts/:id" element={<CrmContactDetail user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/contacts" element={<CrmContacts user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/opportunities/:id" element={<CrmOpportunityDetail user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/opportunities" element={<CrmOpportunities user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/new-client" element={<CrmNewClient user={user} onMenuToggle={toggleSidebar} />} />
          {canAccess(user, 'manager') && <Route path="/review" element={<CrmReviewClients user={user} onMenuToggle={toggleSidebar} />} />}
          <Route path="/tasks" element={<CrmTasks user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/notifications" element={<CrmNotifications user={user} onMenuToggle={toggleSidebar} />} />
          <Route path="/guide" element={<CrmGuide user={user} onMenuToggle={toggleSidebar} />} />
          {canAccess(user, 'manager') && <Route path="/data-quality" element={<CrmDataQuality user={user} onMenuToggle={toggleSidebar} />} />}
          {canAccess(user, 'manager') && <Route path="/admin" element={<AdminPage user={user} onMenuToggle={toggleSidebar} />} />}
        </Routes>
      </div>
    </div>
  );
}
